import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Tab,
  Tabs,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  InputAdornment,
  Collapse,
  IconButton,
} from '@mui/material'
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import {
  Send as SendIcon,
  GetApp as GetAppIcon,
  Receipt as ReceiptIcon,
  Settings as SettingsIcon,
  Timeline as TimelineIcon,
  AddCircle as AddCircleIcon,
  Delete as DeleteIcon,
  Payment as PaymentIcon,
  AccountBalance as AccountBalanceIcon,
  CreditCard as CreditCardIcon,
  Edit as EditIcon,
  Block as BlockIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material'
import { useAppStore, useSnackbars } from '@assembly/hooks'
import { AssemblyLineSearchAndSelect } from '@assembly/components'
import { SnackbarType } from '@assembly/contexts'
import moment from 'moment-timezone'
import {
  usageBillingApi,
  UsageType,
  UsageEventRequest,
  UsageDataResponse,
  UsageDataItem,
  InvoicePreviewResponse,
  InvoiceLineItem,
  SubscriptionInfoResponse,
  SubscriptionDetailsResponse,
  CreditGrant,
  CreditBalanceSummary,
  CreditGrantRequest,
  CreditGrantUpdateRequest,
} from '@assembly/api/usageBilling'

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`usage-billing-tabpanel-${index}`}
      aria-labelledby={`usage-billing-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  )
}

// Storage keys
const STORAGE_KEYS = {
  recordUsage: 'usageBilling_recordUsage',
  currentUsage: 'usageBilling_currentUsage',
  periodUsage: 'usageBilling_periodUsage',
  invoicePreview: 'usageBilling_invoicePreview',
  createSubscription: 'usageBilling_createSubscription',
  clearSubscription: 'usageBilling_clearSubscription',
  addUsageBilling: 'usageBilling_addUsageBilling',
  createMonthly: 'usageBilling_createMonthly',
  subscriptionDetails: 'usageBilling_subscriptionDetails',
  creditGrant: 'usageBilling_creditGrant',
  creditBalance: 'usageBilling_creditBalance',
  creditGrants: 'usageBilling_creditGrants',
}

export default function UsageBilling() {
  const { setAppBarTitle } = useAppStore()
  const { addAlert } = useSnackbars()
  const params = useParams<{ id?: string }>()
  const [activeTab, setActiveTab] = useState(0)
  const [loading, setLoading] = useState(false)

  // Helper function to load from localStorage
  const loadFromStorage = <T,>(key: string, defaultValue: T): T => {
    try {
      const saved = localStorage.getItem(key)
      if (saved) {
        return JSON.parse(saved)
      }
    } catch (e) {
      // Ignore parse errors
    }
    return defaultValue
  }

  // Get assembly ID from URL params
  const urlAssemblyId = params.id || ''

  // Record Usage state - initialize from localStorage or URL param
  const [recordUsageForm, setRecordUsageForm] = useState<UsageEventRequest>(() => {
    const saved = loadFromStorage(STORAGE_KEYS.recordUsage, {
      assemblyId: '',
      usageType: UsageType.LLM_INPUT_TOKENS,
      value: 0,
    })
    // Override with URL param if present
    if (params.id) {
      saved.assemblyId = params.id
    }
    return saved
  })
  const [recordUsageResult, setRecordUsageResult] = useState<string | null>(null)

  // Current Usage state - initialize from localStorage or URL param
  const savedCurrentUsage = loadFromStorage(STORAGE_KEYS.currentUsage, { assemblyId: '', usageType: '' })
  const [currentUsageAssemblyId, setCurrentUsageAssemblyId] = useState(params.id || savedCurrentUsage.assemblyId || '')
  const [currentUsageType, setCurrentUsageType] = useState<string>(savedCurrentUsage.usageType || '')
  const [currentUsageData, setCurrentUsageData] = useState<UsageDataResponse | null>(null)

  // Period Usage state - initialize from localStorage or URL param
  const savedPeriodUsage = loadFromStorage(STORAGE_KEYS.periodUsage, {
    assemblyId: '',
    usageType: '',
    startTimestamp: '',
    endTimestamp: '',
  })
  const [periodUsageAssemblyId, setPeriodUsageAssemblyId] = useState(params.id || savedPeriodUsage.assemblyId || '')
  const [periodUsageType, setPeriodUsageType] = useState<string>(savedPeriodUsage.usageType || '')
  const [startTimestamp, setStartTimestamp] = useState(savedPeriodUsage.startTimestamp || '')
  const [endTimestamp, setEndTimestamp] = useState(savedPeriodUsage.endTimestamp || '')
  const [periodUsageData, setPeriodUsageData] = useState<UsageDataResponse | null>(null)

  // Invoice Preview state - initialize from localStorage or URL param
  const savedInvoicePreview = loadFromStorage(STORAGE_KEYS.invoicePreview, { assemblyId: '' })
  const [invoicePreviewAssemblyId, setInvoicePreviewAssemblyId] = useState(params.id || savedInvoicePreview.assemblyId || '')
  const [invoicePreviewData, setInvoicePreviewData] = useState<InvoicePreviewResponse | null>(null)

  // Create Subscription and Usage Billing state - initialize from localStorage or URL param
  const savedCreateSubscription = loadFromStorage(STORAGE_KEYS.createSubscription, { assemblyId: '' })
  const [createSubscriptionAssemblyId, setCreateSubscriptionAssemblyId] = useState(params.id || savedCreateSubscription.assemblyId || '')
  const [createSubscriptionData, setCreateSubscriptionData] = useState<SubscriptionInfoResponse | null>(null)

  // Clear Subscription state - initialize from localStorage or URL param
  const savedClearSubscription = loadFromStorage(STORAGE_KEYS.clearSubscription, { assemblyId: '' })
  const [clearSubscriptionAssemblyId, setClearSubscriptionAssemblyId] = useState(params.id || savedClearSubscription.assemblyId || '')
  const [clearSubscriptionResult, setClearSubscriptionResult] = useState<string | null>(null)

  // Add Usage Billing state - initialize from localStorage or URL param
  const savedAddUsageBilling = loadFromStorage(STORAGE_KEYS.addUsageBilling, { assemblyId: '' })
  const [addUsageBillingAssemblyId, setAddUsageBillingAssemblyId] = useState(params.id || savedAddUsageBilling.assemblyId || '')
  const [addUsageBillingData, setAddUsageBillingData] = useState<SubscriptionInfoResponse | null>(null)

  // Create Monthly Subscription state - initialize from localStorage or URL param
  const savedCreateMonthly = loadFromStorage(STORAGE_KEYS.createMonthly, { assemblyId: '' })
  const [createMonthlyAssemblyId, setCreateMonthlyAssemblyId] = useState(params.id || savedCreateMonthly.assemblyId || '')
  const [createMonthlyData, setCreateMonthlyData] = useState<SubscriptionInfoResponse | null>(null)

  // Subscription Details state - initialize from localStorage or URL param
  const savedSubscriptionDetails = loadFromStorage(STORAGE_KEYS.subscriptionDetails, { assemblyId: '' })
  const [subscriptionDetailsAssemblyId, setSubscriptionDetailsAssemblyId] = useState(params.id || savedSubscriptionDetails.assemblyId || '')
  const [subscriptionDetailsData, setSubscriptionDetailsData] = useState<SubscriptionDetailsResponse | null>(null)

  // Credit Grant state - initialize from localStorage or URL param
  const savedCreditGrant = loadFromStorage<{
    assemblyId: string
    amountInDollars: string
    currency: string
    category: string
    name?: string | null
    effectiveAt?: string | null
    expiresAt?: string | null
    priority?: number | null
  }>(STORAGE_KEYS.creditGrant, { assemblyId: '', amountInDollars: '', currency: 'usd', category: 'paid' })
  const [creditGrantAssemblyId, setCreditGrantAssemblyId] = useState(params.id || savedCreditGrant.assemblyId || '')
  const [creditGrantAmountDollars, setCreditGrantAmountDollars] = useState<string>(savedCreditGrant.amountInDollars || '')
  const [creditGrantEffectiveAt, setCreditGrantEffectiveAt] = useState<moment.Moment | null>(
    savedCreditGrant.effectiveAt ? moment(savedCreditGrant.effectiveAt) : null
  )
  const [creditGrantExpiresAt, setCreditGrantExpiresAt] = useState<moment.Moment | null>(
    savedCreditGrant.expiresAt ? moment(savedCreditGrant.expiresAt) : null
  )
  const [creditGrantForm, setCreditGrantForm] = useState<Omit<CreditGrantRequest, 'amountInCents' | 'effectiveAt' | 'expiresAt'>>({
    currency: savedCreditGrant.currency || 'usd',
    category: (savedCreditGrant.category === 'paid' || savedCreditGrant.category === 'promotional' ? savedCreditGrant.category : 'paid') as 'paid' | 'promotional',
    name: savedCreditGrant.name || null,
    priority: savedCreditGrant.priority || null,
  })
  const [creditGrantResult, setCreditGrantResult] = useState<CreditGrant | null>(null)

  // Credit Balance state
  const savedCreditBalance = loadFromStorage(STORAGE_KEYS.creditBalance, { assemblyId: '' })
  const [creditBalanceAssemblyId, setCreditBalanceAssemblyId] = useState(params.id || savedCreditBalance.assemblyId || '')
  const [creditBalanceData, setCreditBalanceData] = useState<CreditBalanceSummary | null>(null)

  // Credit Grants List state
  const savedCreditGrants = loadFromStorage(STORAGE_KEYS.creditGrants, { assemblyId: '' })
  const [creditGrantsAssemblyId, setCreditGrantsAssemblyId] = useState(params.id || savedCreditGrants.assemblyId || '')
  const [creditGrantsList, setCreditGrantsList] = useState<CreditGrant[]>([])
  const [selectedCreditGrant, setSelectedCreditGrant] = useState<CreditGrant | null>(null)
  const [updateCreditGrantExpiresAt, setUpdateCreditGrantExpiresAt] = useState<moment.Moment | null>(null)
  const [updateCreditGrantForm, setUpdateCreditGrantForm] = useState<Omit<CreditGrantUpdateRequest, 'expiresAt'>>({
    metadata: {},
  })
  const [invoicePreviewJsonExpanded, setInvoicePreviewJsonExpanded] = useState(false)

  // Set app bar title on mount
  React.useEffect(() => {
    setAppBarTitle('Usage Billing')
  }, [])

  // Persist Record Usage form (only if form has values)
  React.useEffect(() => {
    if (recordUsageForm.assemblyId || recordUsageForm.value > 0) {
      localStorage.setItem(STORAGE_KEYS.recordUsage, JSON.stringify(recordUsageForm))
    }
  }, [recordUsageForm])

  // Persist Current Usage
  React.useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.currentUsage, JSON.stringify({ assemblyId: currentUsageAssemblyId, usageType: currentUsageType }))
  }, [currentUsageAssemblyId, currentUsageType])

  // Persist Period Usage
  React.useEffect(() => {
    localStorage.setItem(
      STORAGE_KEYS.periodUsage,
      JSON.stringify({
        assemblyId: periodUsageAssemblyId,
        usageType: periodUsageType,
        startTimestamp,
        endTimestamp,
      })
    )
  }, [periodUsageAssemblyId, periodUsageType, startTimestamp, endTimestamp])

  // Persist Invoice Preview
  React.useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.invoicePreview, JSON.stringify({ assemblyId: invoicePreviewAssemblyId }))
  }, [invoicePreviewAssemblyId])

  // Persist Create Subscription
  React.useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.createSubscription, JSON.stringify({ assemblyId: createSubscriptionAssemblyId }))
  }, [createSubscriptionAssemblyId])

  // Persist Clear Subscription
  React.useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.clearSubscription, JSON.stringify({ assemblyId: clearSubscriptionAssemblyId }))
  }, [clearSubscriptionAssemblyId])

  // Persist Add Usage Billing
  React.useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.addUsageBilling, JSON.stringify({ assemblyId: addUsageBillingAssemblyId }))
  }, [addUsageBillingAssemblyId])

  // Persist Create Monthly Subscription
  React.useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.createMonthly, JSON.stringify({ assemblyId: createMonthlyAssemblyId }))
  }, [createMonthlyAssemblyId])

  // Persist Subscription Details
  React.useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.subscriptionDetails, JSON.stringify({ assemblyId: subscriptionDetailsAssemblyId }))
  }, [subscriptionDetailsAssemblyId])

  // Persist Credit Grant
  React.useEffect(() => {
    localStorage.setItem(
      STORAGE_KEYS.creditGrant,
      JSON.stringify({
        assemblyId: creditGrantAssemblyId,
        amountInDollars: creditGrantAmountDollars,
        currency: creditGrantForm.currency,
        category: creditGrantForm.category,
        name: creditGrantForm.name,
        effectiveAt: creditGrantEffectiveAt ? creditGrantEffectiveAt.toISOString() : null,
        expiresAt: creditGrantExpiresAt ? creditGrantExpiresAt.toISOString() : null,
        priority: creditGrantForm.priority,
      })
    )
  }, [creditGrantAssemblyId, creditGrantAmountDollars, creditGrantForm, creditGrantEffectiveAt, creditGrantExpiresAt])

  // Persist Credit Balance
  React.useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.creditBalance, JSON.stringify({ assemblyId: creditBalanceAssemblyId }))
  }, [creditBalanceAssemblyId])

  // Persist Credit Grants List
  React.useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.creditGrants, JSON.stringify({ assemblyId: creditGrantsAssemblyId }))
  }, [creditGrantsAssemblyId])

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue)
    // Clear results when switching tabs
    setRecordUsageResult(null)
    setCurrentUsageData(null)
    setPeriodUsageData(null)
    setInvoicePreviewData(null)
    setCreateSubscriptionData(null)
    setClearSubscriptionResult(null)
    setAddUsageBillingData(null)
    setCreateMonthlyData(null)
    setSubscriptionDetailsData(null)
    setCreditGrantResult(null)
    setCreditBalanceData(null)
    setCreditGrantsList([])
    setSelectedCreditGrant(null)
  }

  // Record Usage Event
  const handleRecordUsage = async () => {
    if (!recordUsageForm.assemblyId || !recordUsageForm.usageType || recordUsageForm.value < 1) {
      addAlert({ message: 'Please fill in all required fields. Value must be at least 1.', type: SnackbarType.Error })
      return
    }

    try {
      setLoading(true)
      const response = await usageBillingApi.recordUsage(recordUsageForm)
      setRecordUsageResult(`Success: ${response.data.message}`)
      addAlert({ message: 'Usage event recorded successfully', type: SnackbarType.Success })
      // Reset only the value field, keep assemblyId and usageType for next entry
      setRecordUsageForm({
        ...recordUsageForm,
        value: 0,
      })
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to record usage event'
      setRecordUsageResult(`Error: ${errorMessage}`)
      addAlert({ message: errorMessage, type: SnackbarType.Error })
    } finally {
      setLoading(false)
    }
  }

  // Get Current Usage
  const handleGetCurrentUsage = async () => {
    if (!currentUsageAssemblyId) {
      addAlert({ message: 'Please enter an Assembly ID', type: SnackbarType.Error })
      return
    }

    try {
      setLoading(true)
      const usageType = currentUsageType ? (currentUsageType as UsageType) : undefined
      const response = await usageBillingApi.getCurrentUsage(currentUsageAssemblyId, usageType)
      setCurrentUsageData(response.data)
      addAlert({ message: 'Usage data retrieved successfully', type: SnackbarType.Success })
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to retrieve usage data'
      addAlert({ message: errorMessage, type: SnackbarType.Error })
      setCurrentUsageData(null)
    } finally {
      setLoading(false)
    }
  }

  // Get Usage for Period
  const handleGetPeriodUsage = async () => {
    if (!periodUsageAssemblyId || !startTimestamp || !endTimestamp) {
      addAlert({ message: 'Please fill in all required fields', type: SnackbarType.Error })
      return
    }

    const start = moment(startTimestamp).unix()
    const end = moment(endTimestamp).unix()

    if (start >= end) {
      addAlert({ message: 'Start timestamp must be before end timestamp', type: SnackbarType.Error })
      return
    }

    try {
      setLoading(true)
      const usageType = periodUsageType ? (periodUsageType as UsageType) : undefined
      const response = await usageBillingApi.getUsageForPeriod(
        periodUsageAssemblyId,
        start,
        end,
        usageType
      )
      setPeriodUsageData(response.data)
      addAlert({ message: 'Usage data retrieved successfully', type: SnackbarType.Success })
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to retrieve usage data'
      addAlert({ message: errorMessage, type: SnackbarType.Error })
      setPeriodUsageData(null)
    } finally {
      setLoading(false)
    }
  }

  // Get Invoice Preview
  const handleGetInvoicePreview = async () => {
    if (!invoicePreviewAssemblyId) {
      addAlert({ message: 'Please enter an Assembly ID', type: SnackbarType.Error })
      return
    }

    try {
      setLoading(true)
      const response = await usageBillingApi.getInvoicePreview(invoicePreviewAssemblyId)
      const responseData = response.data

      // Handle different response formats
      let invoiceData: InvoicePreviewResponse
      let lineItemsArray: InvoiceLineItem[] = []

      // If responseData.lineItems is a Stripe list object with 'data' array
      if (responseData.lineItems && typeof responseData.lineItems === 'object' && 'object' in responseData.lineItems && responseData.lineItems.object === 'list' && 'data' in responseData.lineItems && Array.isArray(responseData.lineItems.data)) {
        lineItemsArray = responseData.lineItems.data as InvoiceLineItem[]
      }
      // If responseData has a 'data' array directly (Stripe list format at root)
      else if (responseData.data && Array.isArray(responseData.data)) {
        lineItemsArray = responseData.data
      }
      // If responseData.lineItems is already an array
      else if (responseData.lineItems && Array.isArray(responseData.lineItems)) {
        lineItemsArray = responseData.lineItems
      }
      // If responseData is an array directly
      else if (Array.isArray(responseData)) {
        lineItemsArray = responseData
      }

      // Calculate total amount from line items if not provided
      const totalAmount = responseData.amount !== undefined
        ? responseData.amount
        : lineItemsArray.reduce((sum: number, item: InvoiceLineItem) => sum + (item.amount || 0), 0)
      const currency = responseData.currency || (lineItemsArray.length > 0 ? lineItemsArray[0].currency : 'usd')

      invoiceData = {
        id: responseData.id || '',
        amount: totalAmount,
        currency: currency,
        status: responseData.status || 'draft',
        lineItems: lineItemsArray,
        data: lineItemsArray, // Keep for JSON display
      }

      setInvoicePreviewData(invoiceData)
      addAlert({ message: 'Invoice preview retrieved successfully', type: SnackbarType.Success })
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to retrieve invoice preview'
      addAlert({ message: errorMessage, type: SnackbarType.Error })
      setInvoicePreviewData(null)
    } finally {
      setLoading(false)
    }
  }

  // Helper function to extract usage type from line item
  const getUsageTypeFromLineItem = (item: InvoiceLineItem): string | null => {
    const nickname = item.price?.nickname || item.plan?.nickname || ''
    if (nickname.toLowerCase().includes('input token')) return 'LLM Input Tokens'
    if (nickname.toLowerCase().includes('output token')) return 'LLM Output Tokens'
    if (nickname.toLowerCase().includes('conversation voice') || nickname.toLowerCase().includes('voice agent')) return 'Conversation Voice'
    return null
  }

  // Helper function to copy JSON to clipboard
  const handleCopyJson = (json: any) => {
    navigator.clipboard.writeText(JSON.stringify(json, null, 2))
    addAlert({ message: 'JSON copied to clipboard', type: SnackbarType.Success })
  }

  // Create Subscription and Usage Billing
  const handleCreateSubscriptionAndUsageBilling = async () => {
    if (!createSubscriptionAssemblyId) {
      addAlert({ message: 'Please enter an Assembly ID', type: SnackbarType.Error })
      return
    }

    try {
      setLoading(true)
      const response = await usageBillingApi.createSubscriptionAndUsageBilling(createSubscriptionAssemblyId)
      setCreateSubscriptionData(response.data)
      addAlert({ message: 'Subscription and usage billing created successfully', type: SnackbarType.Success })
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to create subscription and usage billing'
      addAlert({ message: errorMessage, type: SnackbarType.Error })
      setCreateSubscriptionData(null)
    } finally {
      setLoading(false)
    }
  }

  // Clear Subscription
  const handleClearSubscription = async () => {
    if (!clearSubscriptionAssemblyId) {
      addAlert({ message: 'Please enter an Assembly ID', type: SnackbarType.Error })
      return
    }

    try {
      setLoading(true)
      const response = await usageBillingApi.clearSubscription(clearSubscriptionAssemblyId)
      setClearSubscriptionResult(`Success: ${response.data.message}`)
      addAlert({ message: 'Subscription cleared successfully', type: SnackbarType.Success })
      // Don't clear assembly ID - keep it for potential future operations
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to clear subscription'
      setClearSubscriptionResult(`Error: ${errorMessage}`)
      addAlert({ message: errorMessage, type: SnackbarType.Error })
    } finally {
      setLoading(false)
    }
  }

  // Add Usage Billing to Subscription
  const handleAddUsageBillingToSubscription = async () => {
    if (!addUsageBillingAssemblyId) {
      addAlert({ message: 'Please enter an Assembly ID', type: SnackbarType.Error })
      return
    }

    try {
      setLoading(true)
      const response = await usageBillingApi.addUsageBillingToSubscription(addUsageBillingAssemblyId)
      setAddUsageBillingData(response.data)
      addAlert({ message: 'Usage billing added to subscription successfully', type: SnackbarType.Success })
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to add usage billing to subscription'
      addAlert({ message: errorMessage, type: SnackbarType.Error })
      setAddUsageBillingData(null)
    } finally {
      setLoading(false)
    }
  }

  // Create Monthly Subscription
  const handleCreateMonthlySubscription = async () => {
    if (!createMonthlyAssemblyId) {
      addAlert({ message: 'Please enter an Assembly ID', type: SnackbarType.Error })
      return
    }

    try {
      setLoading(true)
      const response = await usageBillingApi.createMonthlySubscription(createMonthlyAssemblyId)
      setCreateMonthlyData(response.data)
      addAlert({ message: 'Monthly subscription created successfully', type: SnackbarType.Success })
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to create monthly subscription'
      addAlert({ message: errorMessage, type: SnackbarType.Error })
      setCreateMonthlyData(null)
    } finally {
      setLoading(false)
    }
  }

  // Get Subscription Details
  const handleGetSubscriptionDetails = async () => {
    if (!subscriptionDetailsAssemblyId) {
      addAlert({ message: 'Please enter an Assembly ID', type: SnackbarType.Error })
      return
    }

    try {
      setLoading(true)
      const response = await usageBillingApi.getSubscriptionDetails(subscriptionDetailsAssemblyId)
      setSubscriptionDetailsData(response.data)
      addAlert({ message: 'Subscription details retrieved successfully', type: SnackbarType.Success })
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to retrieve subscription details'
      addAlert({ message: errorMessage, type: SnackbarType.Error })
      setSubscriptionDetailsData(null)
    } finally {
      setLoading(false)
    }
  }

  // Credit Grant handlers
  const handleCreateCreditGrant = async () => {
    const amountDollars = parseFloat(creditGrantAmountDollars)
    if (!creditGrantAssemblyId || !amountDollars || amountDollars <= 0) {
      addAlert({ message: 'Please fill in all required fields. Amount must be greater than $0.', type: SnackbarType.Error })
      return
    }

    try {
      setLoading(true)
      // Convert dollars to cents
      const amountInCents = Math.round(amountDollars * 100)
      // Convert dates to Unix timestamps
      const effectiveAtUnix = creditGrantEffectiveAt ? creditGrantEffectiveAt.unix() : null
      const expiresAtUnix = creditGrantExpiresAt ? creditGrantExpiresAt.unix() : null

      const requestData: CreditGrantRequest = {
        amountInCents,
        currency: creditGrantForm.currency || 'usd',
        category: creditGrantForm.category || 'paid',
        name: creditGrantForm.name || null,
        effectiveAt: effectiveAtUnix,
        expiresAt: expiresAtUnix,
        priority: creditGrantForm.priority || null,
      }

      const response = await usageBillingApi.createCreditGrant(creditGrantAssemblyId, requestData)
      setCreditGrantResult(response.data)
      addAlert({ message: 'Credit grant created successfully', type: SnackbarType.Success })
      // Reset form but keep assembly ID
      setCreditGrantAmountDollars('')
      setCreditGrantEffectiveAt(null)
      setCreditGrantExpiresAt(null)
      setCreditGrantForm({
        currency: creditGrantForm.currency || 'usd',
        category: creditGrantForm.category || 'paid',
        name: null,
        priority: null,
      })
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to create credit grant'
      addAlert({ message: errorMessage, type: SnackbarType.Error })
      setCreditGrantResult(null)
    } finally {
      setLoading(false)
    }
  }

  const handleGetCreditBalance = async () => {
    if (!creditBalanceAssemblyId) {
      addAlert({ message: 'Please enter an Assembly ID', type: SnackbarType.Error })
      return
    }

    try {
      setLoading(true)
      const response = await usageBillingApi.getCreditBalanceSummary(creditBalanceAssemblyId)
      setCreditBalanceData(response.data)
      addAlert({ message: 'Credit balance retrieved successfully', type: SnackbarType.Success })
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to retrieve credit balance'
      addAlert({ message: errorMessage, type: SnackbarType.Error })
      setCreditBalanceData(null)
    } finally {
      setLoading(false)
    }
  }

  const handleListCreditGrants = async () => {
    if (!creditGrantsAssemblyId) {
      addAlert({ message: 'Please enter an Assembly ID', type: SnackbarType.Error })
      return
    }

    try {
      setLoading(true)
      const response = await usageBillingApi.listCreditGrants(creditGrantsAssemblyId)
      setCreditGrantsList(response.data)
      addAlert({ message: 'Credit grants retrieved successfully', type: SnackbarType.Success })
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to retrieve credit grants'
      addAlert({ message: errorMessage, type: SnackbarType.Error })
      setCreditGrantsList([])
    } finally {
      setLoading(false)
    }
  }

  const handleExpireCreditGrant = async (creditGrantId: string) => {
    if (!creditGrantsAssemblyId) {
      addAlert({ message: 'Please enter an Assembly ID', type: SnackbarType.Error })
      return
    }

    try {
      setLoading(true)
      await usageBillingApi.expireCreditGrant(creditGrantsAssemblyId, creditGrantId)
      addAlert({ message: 'Credit grant expired successfully', type: SnackbarType.Success })
      // Refresh the list
      await handleListCreditGrants()
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to expire credit grant'
      addAlert({ message: errorMessage, type: SnackbarType.Error })
    } finally {
      setLoading(false)
    }
  }

  const handleVoidCreditGrant = async (creditGrantId: string) => {
    if (!creditGrantsAssemblyId) {
      addAlert({ message: 'Please enter an Assembly ID', type: SnackbarType.Error })
      return
    }

    try {
      setLoading(true)
      await usageBillingApi.voidCreditGrant(creditGrantsAssemblyId, creditGrantId)
      addAlert({ message: 'Credit grant voided successfully', type: SnackbarType.Success })
      // Refresh the list
      await handleListCreditGrants()
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to void credit grant'
      addAlert({ message: errorMessage, type: SnackbarType.Error })
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateCreditGrant = async () => {
    if (!creditGrantsAssemblyId || !selectedCreditGrant) {
      addAlert({ message: 'Please select a credit grant to update', type: SnackbarType.Error })
      return
    }

    try {
      setLoading(true)
      // Convert date to Unix timestamp
      const expiresAtUnix = updateCreditGrantExpiresAt ? updateCreditGrantExpiresAt.unix() : null
      const requestData: CreditGrantUpdateRequest = {
        expiresAt: expiresAtUnix,
        metadata: updateCreditGrantForm.metadata,
      }
      const response = await usageBillingApi.updateCreditGrant(creditGrantsAssemblyId, selectedCreditGrant.id, requestData)
      addAlert({ message: 'Credit grant updated successfully', type: SnackbarType.Success })
      // Refresh the list
      await handleListCreditGrants()
      setSelectedCreditGrant(null)
      setUpdateCreditGrantExpiresAt(null)
      setUpdateCreditGrantForm({ metadata: {} })
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to update credit grant'
      addAlert({ message: errorMessage, type: SnackbarType.Error })
    } finally {
      setLoading(false)
    }
  }

  const formatTimestamp = (timestamp: number) => {
    return moment.unix(timestamp).format('YYYY-MM-DD HH:mm:ss')
  }

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100)
  }

  return (
    <Box>
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
          <Tab icon={<SendIcon />} iconPosition="start" label="Record Usage" />
          <Tab icon={<TimelineIcon />} iconPosition="start" label="Current Usage" />
          <Tab icon={<GetAppIcon />} iconPosition="start" label="Period Usage" />
          <Tab icon={<ReceiptIcon />} iconPosition="start" label="Invoice Preview" />
          <Tab icon={<SettingsIcon />} iconPosition="start" label="Create Subscription & Usage Billing" />
          <Tab icon={<AddCircleIcon />} iconPosition="start" label="Add Usage Billing" />
          <Tab icon={<PaymentIcon />} iconPosition="start" label="Create Monthly Subscription" />
          <Tab icon={<DeleteIcon />} iconPosition="start" label="Clear Subscription" />
          <Tab icon={<ReceiptIcon />} iconPosition="start" label="Subscription Details" />
          <Tab icon={<CreditCardIcon />} iconPosition="start" label="Create Credit Grant" />
          <Tab icon={<AccountBalanceIcon />} iconPosition="start" label="Credit Balance" />
          <Tab icon={<EditIcon />} iconPosition="start" label="Manage Credit Grants" />
        </Tabs>
      </Paper>

      {/* Record Usage Tab */}
      <TabPanel value={activeTab} index={0}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Record Usage Event
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Record a usage event for an assembly line (LLM tokens or Conversation Voice)
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <AssemblyLineSearchAndSelect
                  label="Assembly Line"
                  value={recordUsageForm.assemblyId}
                  onChange={(id) =>
                    setRecordUsageForm({ ...recordUsageForm, assemblyId: id })
                  }
                  required
                  placeholder="Search assembly lines"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Usage Type</InputLabel>
                  <Select
                    value={recordUsageForm.usageType}
                    label="Usage Type"
                    onChange={(e: SelectChangeEvent) =>
                      setRecordUsageForm({
                        ...recordUsageForm,
                        usageType: e.target.value as UsageType,
                      })
                    }
                  >
                    <MenuItem value={UsageType.LLM_INPUT_TOKENS}>LLM Input Tokens</MenuItem>
                    <MenuItem value={UsageType.LLM_OUTPUT_TOKENS}>LLM Output Tokens</MenuItem>
                    <MenuItem value={UsageType.CONVERSATION_VOICE}>Conversation Voice</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Value"
                  type="number"
                  value={recordUsageForm.value || ''}
                  onChange={(e) =>
                    setRecordUsageForm({
                      ...recordUsageForm,
                      value: parseInt(e.target.value) || 0,
                    })
                  }
                  required
                  inputProps={{ min: 1 }}
                  helperText="Number of tokens or minutes"
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  onClick={handleRecordUsage}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
                >
                  Record Usage
                </Button>
              </Grid>
              {recordUsageResult && (
                <Grid item xs={12}>
                  <Alert severity={recordUsageResult.startsWith('Error') ? 'error' : 'success'}>
                    {recordUsageResult}
                  </Alert>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Current Usage Tab */}
      <TabPanel value={activeTab} index={1}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Get Current Usage
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Retrieve current usage data for an assembly line for the current billing period
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <AssemblyLineSearchAndSelect
                  label="Assembly Line"
                  value={currentUsageAssemblyId}
                  onChange={setCurrentUsageAssemblyId}
                  required
                  placeholder="Search assembly lines"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel shrink>Usage Type (Optional)</InputLabel>
                  <Select
                    value={currentUsageType || ''}
                    label="Usage Type (Optional)"
                    onChange={(e: SelectChangeEvent) => setCurrentUsageType(e.target.value)}
                    displayEmpty
                    renderValue={(selected) => {
                      if (selected === '' || !selected) {
                        return <span style={{ color: 'rgba(0, 0, 0, 0.6)' }}>All Types</span>
                      }
                      if (selected === UsageType.LLM_INPUT_TOKENS) return 'LLM Input Tokens'
                      if (selected === UsageType.LLM_OUTPUT_TOKENS) return 'LLM Output Tokens'
                      if (selected === UsageType.CONVERSATION_VOICE) return 'Conversation Voice'
                      return selected
                    }}
                  >
                    <MenuItem value="">All Types</MenuItem>
                    <MenuItem value={UsageType.LLM_INPUT_TOKENS}>LLM Input Tokens</MenuItem>
                    <MenuItem value={UsageType.LLM_OUTPUT_TOKENS}>LLM Output Tokens</MenuItem>
                    <MenuItem value={UsageType.CONVERSATION_VOICE}>Conversation Voice</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  onClick={handleGetCurrentUsage}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <TimelineIcon />}
                >
                  Get Current Usage
                </Button>
              </Grid>
              {currentUsageData && (
                <Grid item xs={12}>
                  {/* Metadata Section */}
                  {currentUsageData.metadata && (
                    <Card variant="outlined" sx={{ mb: 2 }}>
                      <CardContent>
                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                          Subscription Metadata
                        </Typography>
                        <Grid container spacing={2}>
                          {currentUsageData.metadata.scheduleId && (
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2" color="text.secondary">
                                Schedule ID
                              </Typography>
                              <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
                                {currentUsageData.metadata.scheduleId}
                              </Typography>
                            </Grid>
                          )}
                          {currentUsageData.metadata.startDate && (
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2" color="text.secondary">
                                Start Date
                              </Typography>
                              <Typography variant="body1">
                                {formatTimestamp(currentUsageData.metadata.startDate)}
                              </Typography>
                            </Grid>
                          )}
                          {currentUsageData.metadata.status && (
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2" color="text.secondary">
                                Status
                              </Typography>
                              <Chip
                                label={currentUsageData.metadata.status}
                                color={
                                  currentUsageData.metadata.status === 'active'
                                    ? 'success'
                                    : currentUsageData.metadata.status === 'not_started'
                                      ? 'warning'
                                      : 'default'
                                }
                                size="small"
                              />
                            </Grid>
                          )}
                        </Grid>
                      </CardContent>
                    </Card>
                  )}

                  {/* Check if there's any usage data to display */}
                  {(() => {
                    const hasLegacyData = currentUsageData.llm_tokens || currentUsageData.conversation_voice
                    const priceKeys = Object.keys(currentUsageData).filter(
                      (key) => key.startsWith('price_') && key !== 'metadata'
                    )
                    const hasPriceData = priceKeys.some((key) => {
                      const item = currentUsageData[key] as UsageDataItem | undefined
                      return item && item.total !== undefined
                    })
                    const hasUsageData = hasLegacyData || hasPriceData

                    if (!hasUsageData) {
                      return (
                        <Alert severity="info" sx={{ mt: 2 }}>
                          No usage data found for the current billing period.
                          {currentUsageData.metadata && currentUsageData.metadata.status === 'not_started' && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              Subscription status: {currentUsageData.metadata.status}
                            </Typography>
                          )}
                        </Alert>
                      )
                    }

                    return (
                      <TableContainer component={Paper} sx={{ mt: 2 }}>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Usage Type / Price ID</TableCell>
                              <TableCell align="right">Total</TableCell>
                              <TableCell>Start Time</TableCell>
                              <TableCell>End Time</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {/* Legacy format: llm_tokens */}
                            {currentUsageData.llm_tokens && (
                              <TableRow>
                                <TableCell>
                                  <Chip label="LLM Tokens" color="primary" size="small" />
                                </TableCell>
                                <TableCell align="right">{currentUsageData.llm_tokens.total}</TableCell>
                                <TableCell>
                                  {formatTimestamp(
                                    currentUsageData.llm_tokens.startTime || currentUsageData.llm_tokens.start_time || 0
                                  )}
                                </TableCell>
                                <TableCell>
                                  {formatTimestamp(
                                    currentUsageData.llm_tokens.endTime || currentUsageData.llm_tokens.end_time || 0
                                  )}
                                </TableCell>
                              </TableRow>
                            )}
                            {/* Legacy format: conversation_voice */}
                            {currentUsageData.conversation_voice && (
                              <TableRow>
                                <TableCell>
                                  <Chip label="Conversation Voice" color="secondary" size="small" />
                                </TableCell>
                                <TableCell align="right">{currentUsageData.conversation_voice.total}</TableCell>
                                <TableCell>
                                  {formatTimestamp(
                                    currentUsageData.conversation_voice.startTime ||
                                    currentUsageData.conversation_voice.start_time ||
                                    0
                                  )}
                                </TableCell>
                                <TableCell>
                                  {formatTimestamp(
                                    currentUsageData.conversation_voice.endTime ||
                                    currentUsageData.conversation_voice.end_time ||
                                    0
                                  )}
                                </TableCell>
                              </TableRow>
                            )}
                            {/* New format: price_xxxxx keys */}
                            {priceKeys.map((key) => {
                              const item = currentUsageData[key] as UsageDataItem | undefined
                              if (!item || item.total === undefined) return null
                              return (
                                <TableRow key={key}>
                                  <TableCell>
                                    <Chip label={key} color="info" size="small" />
                                  </TableCell>
                                  <TableCell align="right">{item.total}</TableCell>
                                  <TableCell>
                                    {formatTimestamp(item.startTime || item.start_time || 0)}
                                  </TableCell>
                                  <TableCell>{formatTimestamp(item.endTime || item.end_time || 0)}</TableCell>
                                </TableRow>
                              )
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )
                  })()}
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Period Usage Tab */}
      <TabPanel value={activeTab} index={2}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Get Usage for Custom Period
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Retrieve usage data for a custom time period
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <AssemblyLineSearchAndSelect
                  label="Assembly Line"
                  value={periodUsageAssemblyId}
                  onChange={setPeriodUsageAssemblyId}
                  required
                  placeholder="Search assembly lines"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel shrink>Usage Type (Optional)</InputLabel>
                  <Select
                    value={periodUsageType || ''}
                    label="Usage Type (Optional)"
                    onChange={(e: SelectChangeEvent) => setPeriodUsageType(e.target.value)}
                    displayEmpty
                    renderValue={(selected) => {
                      if (selected === '' || !selected) {
                        return <span style={{ color: 'rgba(0, 0, 0, 0.6)' }}>All Types</span>
                      }
                      if (selected === UsageType.LLM_INPUT_TOKENS) return 'LLM Input Tokens'
                      if (selected === UsageType.LLM_OUTPUT_TOKENS) return 'LLM Output Tokens'
                      if (selected === UsageType.CONVERSATION_VOICE) return 'Conversation Voice'
                      return selected
                    }}
                  >
                    <MenuItem value="">All Types</MenuItem>
                    <MenuItem value={UsageType.LLM_INPUT_TOKENS}>LLM Input Tokens</MenuItem>
                    <MenuItem value={UsageType.LLM_OUTPUT_TOKENS}>LLM Output Tokens</MenuItem>
                    <MenuItem value={UsageType.CONVERSATION_VOICE}>Conversation Voice</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Start Date & Time"
                  type="datetime-local"
                  value={startTimestamp}
                  onChange={(e) => setStartTimestamp(e.target.value)}
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="End Date & Time"
                  type="datetime-local"
                  value={endTimestamp}
                  onChange={(e) => setEndTimestamp(e.target.value)}
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  onClick={handleGetPeriodUsage}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <GetAppIcon />}
                >
                  Get Usage for Period
                </Button>
              </Grid>
              {periodUsageData && (
                <Grid item xs={12}>
                  {/* Metadata Section */}
                  {periodUsageData.metadata && (
                    <Card variant="outlined" sx={{ mb: 2 }}>
                      <CardContent>
                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                          Subscription Metadata
                        </Typography>
                        <Grid container spacing={2}>
                          {periodUsageData.metadata.scheduleId && (
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2" color="text.secondary">
                                Schedule ID
                              </Typography>
                              <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
                                {periodUsageData.metadata.scheduleId}
                              </Typography>
                            </Grid>
                          )}
                          {periodUsageData.metadata.startDate && (
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2" color="text.secondary">
                                Start Date
                              </Typography>
                              <Typography variant="body1">
                                {formatTimestamp(periodUsageData.metadata.startDate)}
                              </Typography>
                            </Grid>
                          )}
                          {periodUsageData.metadata.status && (
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2" color="text.secondary">
                                Status
                              </Typography>
                              <Chip
                                label={periodUsageData.metadata.status}
                                color={
                                  periodUsageData.metadata.status === 'active'
                                    ? 'success'
                                    : periodUsageData.metadata.status === 'not_started'
                                      ? 'warning'
                                      : 'default'
                                }
                                size="small"
                              />
                            </Grid>
                          )}
                        </Grid>
                      </CardContent>
                    </Card>
                  )}

                  {/* Usage Data Table */}
                  <TableContainer component={Paper} sx={{ mt: 2 }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Usage Type / Price ID</TableCell>
                          <TableCell align="right">Total</TableCell>
                          <TableCell>Start Time</TableCell>
                          <TableCell>End Time</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {/* Legacy format: llm_tokens */}
                        {periodUsageData.llm_tokens && (
                          <TableRow>
                            <TableCell>
                              <Chip label="LLM Tokens" color="primary" size="small" />
                            </TableCell>
                            <TableCell align="right">{periodUsageData.llm_tokens.total}</TableCell>
                            <TableCell>
                              {formatTimestamp(
                                periodUsageData.llm_tokens.startTime || periodUsageData.llm_tokens.start_time || 0
                              )}
                            </TableCell>
                            <TableCell>
                              {formatTimestamp(
                                periodUsageData.llm_tokens.endTime || periodUsageData.llm_tokens.end_time || 0
                              )}
                            </TableCell>
                          </TableRow>
                        )}
                        {/* Legacy format: conversation_voice */}
                        {periodUsageData.conversation_voice && (
                          <TableRow>
                            <TableCell>
                              <Chip label="Conversation Voice" color="secondary" size="small" />
                            </TableCell>
                            <TableCell align="right">{periodUsageData.conversation_voice.total}</TableCell>
                            <TableCell>
                              {formatTimestamp(
                                periodUsageData.conversation_voice.startTime ||
                                periodUsageData.conversation_voice.start_time ||
                                0
                              )}
                            </TableCell>
                            <TableCell>
                              {formatTimestamp(
                                periodUsageData.conversation_voice.endTime ||
                                periodUsageData.conversation_voice.end_time ||
                                0
                              )}
                            </TableCell>
                          </TableRow>
                        )}
                        {/* New format: price_xxxxx keys */}
                        {Object.keys(periodUsageData)
                          .filter((key) => key.startsWith('price_') && key !== 'metadata')
                          .map((key) => {
                            const item = periodUsageData[key] as UsageDataItem | undefined
                            if (!item) return null
                            return (
                              <TableRow key={key}>
                                <TableCell>
                                  <Chip label={key} color="info" size="small" />
                                </TableCell>
                                <TableCell align="right">{item.total}</TableCell>
                                <TableCell>
                                  {formatTimestamp(item.startTime || item.start_time || 0)}
                                </TableCell>
                                <TableCell>{formatTimestamp(item.endTime || item.end_time || 0)}</TableCell>
                              </TableRow>
                            )
                          })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Invoice Preview Tab */}
      <TabPanel value={activeTab} index={3}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Get Invoice Preview
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Retrieve a preview of the upcoming invoice including usage charges
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <AssemblyLineSearchAndSelect
                  label="Assembly Line"
                  value={invoicePreviewAssemblyId}
                  onChange={setInvoicePreviewAssemblyId}
                  required
                  placeholder="Search assembly lines"
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  onClick={handleGetInvoicePreview}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <ReceiptIcon />}
                >
                  Get Invoice Preview
                </Button>
              </Grid>
              {invoicePreviewData && (
                <Grid item xs={12}>
                  <Paper sx={{ p: 3, mt: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Invoice Preview
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="text.secondary">
                          Invoice ID
                        </Typography>
                        <Typography variant="body1">{invoicePreviewData.id}</Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="text.secondary">
                          Status
                        </Typography>
                        <Chip
                          label={invoicePreviewData.status}
                          color={
                            invoicePreviewData.status === 'paid'
                              ? 'success'
                              : invoicePreviewData.status === 'draft'
                                ? 'default'
                                : 'warning'
                          }
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="text.secondary">
                          Amount
                        </Typography>
                        <Typography variant="h6">
                          {formatCurrency(invoicePreviewData.amount, invoicePreviewData.currency)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="text.secondary">
                          Currency
                        </Typography>
                        <Typography variant="body1">
                          {invoicePreviewData.currency.toUpperCase()}
                        </Typography>
                      </Grid>
                      {/* Usage Cost Breakdown */}
                      {(() => {
                        const lineItems = invoicePreviewData.lineItems || invoicePreviewData.data || []
                        // Handle Stripe list object format
                        const actualLineItems = Array.isArray(lineItems)
                          ? lineItems
                          : (lineItems && typeof lineItems === 'object' && 'data' in lineItems && Array.isArray(lineItems.data))
                            ? lineItems.data
                            : []
                        if (!Array.isArray(actualLineItems) || actualLineItems.length === 0) {
                          return null
                        }
                        return (
                          <Grid item xs={12}>
                            <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 3, mb: 2 }}>
                              Usage Cost Breakdown
                            </Typography>
                            <TableContainer component={Paper} variant="outlined">
                              <Table>
                                <TableHead>
                                  <TableRow>
                                    <TableCell><strong>Item</strong></TableCell>
                                    <TableCell align="right"><strong>Quantity</strong></TableCell>
                                    <TableCell align="right"><strong>Unit Price</strong></TableCell>
                                    <TableCell align="right"><strong>Total Amount</strong></TableCell>
                                    <TableCell><strong>Period</strong></TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {actualLineItems.map((item: InvoiceLineItem) => {
                                    const usageType = getUsageTypeFromLineItem(item)
                                    const isUsageItem = usageType !== null
                                    // Handle unitAmount - can be null for metered items, use unitAmountDecimal converted to cents
                                    let unitAmount = item.price?.unitAmount || item.plan?.amount || 0
                                    if (unitAmount === null || unitAmount === 0) {
                                      // Try to get from unitAmountDecimal (in dollars) and convert to cents
                                      const unitAmountDecimal = item.price?.unitAmountDecimal || item.plan?.amountDecimal
                                      if (unitAmountDecimal !== null && unitAmountDecimal !== undefined) {
                                        unitAmount = Math.round(unitAmountDecimal * 100)
                                      }
                                    }
                                    const period = item.period
                                      ? `${moment.unix(item.period.start).format('MMM DD, YYYY')} - ${moment.unix(item.period.end).format('MMM DD, YYYY')}`
                                      : 'N/A'

                                    return (
                                      <TableRow key={item.id} sx={{ bgcolor: isUsageItem ? 'action.hover' : 'inherit' }}>
                                        <TableCell>
                                          {usageType ? (
                                            <Box>
                                              <Typography variant="body2" fontWeight="bold">{usageType}</Typography>
                                              <Typography variant="caption" color="text.secondary">
                                                {item.description}
                                              </Typography>
                                            </Box>
                                          ) : (
                                            <Typography variant="body2">{item.description}</Typography>
                                          )}
                                        </TableCell>
                                        <TableCell align="right">{item.quantity.toLocaleString()}</TableCell>
                                        <TableCell align="right">
                                          {formatCurrency(unitAmount, item.currency)}
                                        </TableCell>
                                        <TableCell align="right">
                                          <Typography variant="body2" fontWeight={isUsageItem ? 'bold' : 'normal'}>
                                            {formatCurrency(item.amount, item.currency)}
                                          </Typography>
                                        </TableCell>
                                        <TableCell>
                                          <Typography variant="caption">{period}</Typography>
                                        </TableCell>
                                      </TableRow>
                                    )
                                  })}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Grid>
                        )
                      })()}

                      {/* Summary of Usage Costs */}
                      {(() => {
                        const lineItems = invoicePreviewData.lineItems || invoicePreviewData.data || []
                        // Handle Stripe list object format
                        const actualLineItems = Array.isArray(lineItems)
                          ? lineItems
                          : (lineItems && typeof lineItems === 'object' && 'data' in lineItems && Array.isArray(lineItems.data))
                            ? lineItems.data
                            : []
                        if (!Array.isArray(actualLineItems) || actualLineItems.length === 0) {
                          return null
                        }
                        return (
                          <Grid item xs={12}>
                            <Box sx={{ mt: 3, p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
                              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                Usage Costs Summary
                              </Typography>
                              <Grid container spacing={2}>
                                {['LLM Input Tokens', 'LLM Output Tokens', 'Conversation Voice'].map((usageType) => {
                                  const items = actualLineItems.filter((item: InvoiceLineItem) =>
                                    getUsageTypeFromLineItem(item) === usageType
                                  )
                                  const totalAmount = items.length > 0
                                    ? items.reduce((sum, item) => sum + item.amount, 0)
                                    : 0

                                  return (
                                    <Grid item xs={12} sm={4} key={usageType}>
                                      <Typography variant="body2" color="text.secondary">
                                        {usageType}
                                      </Typography>
                                      <Typography variant="h6">
                                        {formatCurrency(totalAmount, invoicePreviewData.currency)}
                                      </Typography>
                                      {items.length === 0 && (
                                        <Typography variant="caption" color="text.secondary">
                                          No line items found
                                        </Typography>
                                      )}
                                    </Grid>
                                  )
                                })}
                              </Grid>
                            </Box>
                          </Grid>
                        )
                      })()}

                      {/* Collapsible JSON View */}
                      {invoicePreviewData.lineItems && (
                        <Grid item xs={12}>
                          <Box sx={{ mt: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                              <Typography variant="body2" color="text.secondary">
                                Raw JSON Data
                              </Typography>
                              <Box>
                                <IconButton
                                  size="small"
                                  onClick={() => handleCopyJson(invoicePreviewData.lineItems || invoicePreviewData.data || invoicePreviewData)}
                                  title="Copy JSON"
                                >
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => setInvoicePreviewJsonExpanded(!invoicePreviewJsonExpanded)}
                                  title={invoicePreviewJsonExpanded ? 'Collapse' : 'Expand'}
                                >
                                  {invoicePreviewJsonExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                              </Box>
                            </Box>
                            <Collapse in={invoicePreviewJsonExpanded}>
                              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', maxHeight: '500px', overflow: 'auto' }}>
                                <pre style={{ margin: 0, fontSize: '12px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                  {JSON.stringify(invoicePreviewData.lineItems || invoicePreviewData.data || invoicePreviewData, null, 2)}
                                </pre>
                              </Paper>
                            </Collapse>
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Create Subscription and Usage Billing Tab */}
      <TabPanel value={activeTab} index={4}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Create Subscription and Usage-Based Billing
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Creates a full recurring subscription with usage-based billing for an assembly line.
              If subscription and usage billing already exist, returns an error. If subscription exists but usage billing is missing,
              automatically adds usage billing. If subscription does not exist, creates both subscription and usage billing.
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <AssemblyLineSearchAndSelect
                  label="Assembly Line"
                  value={createSubscriptionAssemblyId}
                  onChange={setCreateSubscriptionAssemblyId}
                  required
                  placeholder="Search assembly lines"
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  onClick={handleCreateSubscriptionAndUsageBilling}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <SettingsIcon />}
                >
                  Create Subscription and Usage Billing
                </Button>
              </Grid>
              {createSubscriptionData && (
                <Grid item xs={12}>
                  <Paper sx={{ p: 3, mt: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Subscription Information
                    </Typography>
                    <Grid container spacing={2}>
                      {createSubscriptionData.subscriptionScheduleId && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">
                            Schedule ID
                          </Typography>
                          <Typography variant="body1">
                            {createSubscriptionData.subscriptionScheduleId}
                          </Typography>
                        </Grid>
                      )}
                      {createSubscriptionData.status && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">
                            Status
                          </Typography>
                          <Chip
                            label={createSubscriptionData.status}
                            color={
                              createSubscriptionData.status === 'active'
                                ? 'success'
                                : createSubscriptionData.status === 'canceled'
                                  ? 'error'
                                  : 'default'
                            }
                            size="small"
                          />
                        </Grid>
                      )}
                      {createSubscriptionData.subscriptionId && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">
                            Subscription ID
                          </Typography>
                          <Typography variant="body1">{createSubscriptionData.subscriptionId}</Typography>
                        </Grid>
                      )}
                      {createSubscriptionData.subscriptionStatus && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">
                            Subscription Status
                          </Typography>
                          <Chip
                            label={createSubscriptionData.subscriptionStatus}
                            color={
                              createSubscriptionData.subscriptionStatus === 'active'
                                ? 'success'
                                : createSubscriptionData.subscriptionStatus === 'canceled'
                                  ? 'error'
                                  : 'warning'
                            }
                            size="small"
                          />
                        </Grid>
                      )}
                      {createSubscriptionData.startDate && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">
                            Start Date
                          </Typography>
                          <Typography variant="body1">
                            {formatTimestamp(createSubscriptionData.startDate)}
                          </Typography>
                        </Grid>
                      )}
                      {createSubscriptionData.endBehavior && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">
                            End Behavior
                          </Typography>
                          <Typography variant="body1">{createSubscriptionData.endBehavior}</Typography>
                        </Grid>
                      )}
                      {createSubscriptionData.currentPeriodStart && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">
                            Current Period Start
                          </Typography>
                          <Typography variant="body1">
                            {formatTimestamp(createSubscriptionData.currentPeriodStart)}
                          </Typography>
                        </Grid>
                      )}
                      {createSubscriptionData.currentPeriodEnd && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">
                            Current Period End
                          </Typography>
                          <Typography variant="body1">
                            {formatTimestamp(createSubscriptionData.currentPeriodEnd)}
                          </Typography>
                        </Grid>
                      )}
                      {createSubscriptionData.customerId && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">
                            Customer ID
                          </Typography>
                          <Typography variant="body1">{createSubscriptionData.customerId}</Typography>
                        </Grid>
                      )}
                      {createSubscriptionData.productId && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">
                            Product ID
                          </Typography>
                          <Typography variant="body1">{createSubscriptionData.productId}</Typography>
                        </Grid>
                      )}
                      {createSubscriptionData.productInfo && (
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                            Product Information
                          </Typography>
                          <Grid container spacing={2}>
                            {createSubscriptionData.productInfo.name && (
                              <Grid item xs={12} md={6}>
                                <Typography variant="body2" color="text.secondary">
                                  Product Name
                                </Typography>
                                <Typography variant="body1">
                                  {createSubscriptionData.productInfo.name}
                                </Typography>
                              </Grid>
                            )}
                            {createSubscriptionData.productInfo.sku && (
                              <Grid item xs={12} md={6}>
                                <Typography variant="body2" color="text.secondary">
                                  SKU Type
                                </Typography>
                                <Chip
                                  label={createSubscriptionData.productInfo.sku}
                                  size="small"
                                  color="primary"
                                />
                              </Grid>
                            )}
                            {createSubscriptionData.productInfo.projectType && (
                              <Grid item xs={12} md={6}>
                                <Typography variant="body2" color="text.secondary">
                                  Project Type
                                </Typography>
                                <Typography variant="body1">
                                  {createSubscriptionData.productInfo.projectType}
                                </Typography>
                              </Grid>
                            )}
                            {createSubscriptionData.productInfo.assemblyId && (
                              <Grid item xs={12} md={6}>
                                <Typography variant="body2" color="text.secondary">
                                  Assembly ID
                                </Typography>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                  {createSubscriptionData.productInfo.assemblyId}
                                </Typography>
                              </Grid>
                            )}
                            {createSubscriptionData.productInfo.orgId && (
                              <Grid item xs={12} md={6}>
                                <Typography variant="body2" color="text.secondary">
                                  Organization ID
                                </Typography>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                  {createSubscriptionData.productInfo.orgId}
                                </Typography>
                              </Grid>
                            )}
                          </Grid>
                        </Grid>
                      )}
                      {createSubscriptionData.basePriceId && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">
                            Base Price ID
                          </Typography>
                          <Typography variant="body1">{createSubscriptionData.basePriceId}</Typography>
                        </Grid>
                      )}
                      {createSubscriptionData.setupComplete !== undefined && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">
                            Setup Complete
                          </Typography>
                          <Chip
                            label={createSubscriptionData.setupComplete ? 'Yes' : 'No'}
                            color={createSubscriptionData.setupComplete ? 'success' : 'warning'}
                            size="small"
                          />
                        </Grid>
                      )}
                      {createSubscriptionData.usageBilling && (
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                            Usage Billing
                          </Typography>
                          <Grid container spacing={2}>
                            {createSubscriptionData.usageBilling.llmInputTokensMeterId && (
                              <Grid item xs={12} md={6}>
                                <Typography variant="body2" color="text.secondary">
                                  LLM Input Tokens Meter ID
                                </Typography>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                  {createSubscriptionData.usageBilling.llmInputTokensMeterId}
                                </Typography>
                              </Grid>
                            )}
                            {createSubscriptionData.usageBilling.llmInputTokensPriceId && (
                              <Grid item xs={12} md={6}>
                                <Typography variant="body2" color="text.secondary">
                                  LLM Input Tokens Price ID
                                </Typography>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                  {createSubscriptionData.usageBilling.llmInputTokensPriceId}
                                </Typography>
                              </Grid>
                            )}
                            {createSubscriptionData.usageBilling.llmOutputTokensMeterId && (
                              <Grid item xs={12} md={6}>
                                <Typography variant="body2" color="text.secondary">
                                  LLM Output Tokens Meter ID
                                </Typography>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                  {createSubscriptionData.usageBilling.llmOutputTokensMeterId}
                                </Typography>
                              </Grid>
                            )}
                            {createSubscriptionData.usageBilling.llmOutputTokensPriceId && (
                              <Grid item xs={12} md={6}>
                                <Typography variant="body2" color="text.secondary">
                                  LLM Output Tokens Price ID
                                </Typography>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                  {createSubscriptionData.usageBilling.llmOutputTokensPriceId}
                                </Typography>
                              </Grid>
                            )}
                            {createSubscriptionData.usageBilling.conversationVoiceMeterId && (
                              <Grid item xs={12} md={6}>
                                <Typography variant="body2" color="text.secondary">
                                  Conversation Voice Meter ID
                                </Typography>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                  {createSubscriptionData.usageBilling.conversationVoiceMeterId}
                                </Typography>
                              </Grid>
                            )}
                            {createSubscriptionData.usageBilling.conversationVoicePriceId && (
                              <Grid item xs={12} md={6}>
                                <Typography variant="body2" color="text.secondary">
                                  Conversation Voice Price ID
                                </Typography>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                  {createSubscriptionData.usageBilling.conversationVoicePriceId}
                                </Typography>
                              </Grid>
                            )}
                          </Grid>
                        </Grid>
                      )}
                      {createSubscriptionData.items && createSubscriptionData.items.length > 0 && (
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                            Subscription Items
                          </Typography>
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Item ID</TableCell>
                                  <TableCell>Price ID</TableCell>
                                  <TableCell align="right">Unit Amount</TableCell>
                                  <TableCell>Currency</TableCell>
                                  <TableCell>Usage Type</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {createSubscriptionData.items.map((item, index) => (
                                  <TableRow key={index}>
                                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                                      {item.id}
                                    </TableCell>
                                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                                      {item.priceId}
                                    </TableCell>
                                    <TableCell align="right">
                                      {formatCurrency(item.unitAmount, item.currency)}
                                    </TableCell>
                                    <TableCell>{item.currency.toUpperCase()}</TableCell>
                                    <TableCell>
                                      {item.usageType ? (
                                        <Chip label={item.usageType} size="small" color="info" />
                                      ) : (
                                        <Chip label="Base" size="small" />
                                      )}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Grid>
                      )}
                    </Grid>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Add Usage Billing to Subscription Tab */}
      <TabPanel value={activeTab} index={5}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Add Usage-Based Billing to Existing Subscription
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Adds usage-based billing (meters and prices) to an existing subscription.
              If subscription does not exist, returns an error. If usage billing already exists, returns an error.
              If subscription exists but usage billing is missing, creates meters and prices and adds them to the subscription.
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <AssemblyLineSearchAndSelect
                  label="Assembly Line"
                  value={addUsageBillingAssemblyId}
                  onChange={setAddUsageBillingAssemblyId}
                  required
                  placeholder="Search assembly lines"
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  onClick={handleAddUsageBillingToSubscription}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <AddCircleIcon />}
                >
                  Add Usage Billing to Subscription
                </Button>
              </Grid>
              {addUsageBillingData && (
                <Grid item xs={12}>
                  <Paper sx={{ p: 3, mt: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Subscription Information
                    </Typography>
                    <Grid container spacing={2}>
                      {addUsageBillingData.subscriptionScheduleId && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">
                            Schedule ID
                          </Typography>
                          <Typography variant="body1">
                            {addUsageBillingData.subscriptionScheduleId}
                          </Typography>
                        </Grid>
                      )}
                      {addUsageBillingData.status && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">
                            Status
                          </Typography>
                          <Chip
                            label={addUsageBillingData.status}
                            color={
                              addUsageBillingData.status === 'active'
                                ? 'success'
                                : addUsageBillingData.status === 'canceled'
                                  ? 'error'
                                  : 'default'
                            }
                            size="small"
                          />
                        </Grid>
                      )}
                      {addUsageBillingData.usageBilling && (
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                            Usage Billing
                          </Typography>
                          <Grid container spacing={2}>
                            {addUsageBillingData.usageBilling.llmInputTokensMeterId && (
                              <Grid item xs={12} md={6}>
                                <Typography variant="body2" color="text.secondary">
                                  LLM Input Tokens Meter ID
                                </Typography>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                  {addUsageBillingData.usageBilling.llmInputTokensMeterId}
                                </Typography>
                              </Grid>
                            )}
                            {addUsageBillingData.usageBilling.llmInputTokensPriceId && (
                              <Grid item xs={12} md={6}>
                                <Typography variant="body2" color="text.secondary">
                                  LLM Input Tokens Price ID
                                </Typography>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                  {addUsageBillingData.usageBilling.llmInputTokensPriceId}
                                </Typography>
                              </Grid>
                            )}
                            {addUsageBillingData.usageBilling.llmOutputTokensMeterId && (
                              <Grid item xs={12} md={6}>
                                <Typography variant="body2" color="text.secondary">
                                  LLM Output Tokens Meter ID
                                </Typography>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                  {addUsageBillingData.usageBilling.llmOutputTokensMeterId}
                                </Typography>
                              </Grid>
                            )}
                            {addUsageBillingData.usageBilling.llmOutputTokensPriceId && (
                              <Grid item xs={12} md={6}>
                                <Typography variant="body2" color="text.secondary">
                                  LLM Output Tokens Price ID
                                </Typography>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                  {addUsageBillingData.usageBilling.llmOutputTokensPriceId}
                                </Typography>
                              </Grid>
                            )}
                            {addUsageBillingData.usageBilling.conversationVoiceMeterId && (
                              <Grid item xs={12} md={6}>
                                <Typography variant="body2" color="text.secondary">
                                  Conversation Voice Meter ID
                                </Typography>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                  {addUsageBillingData.usageBilling.conversationVoiceMeterId}
                                </Typography>
                              </Grid>
                            )}
                            {addUsageBillingData.usageBilling.conversationVoicePriceId && (
                              <Grid item xs={12} md={6}>
                                <Typography variant="body2" color="text.secondary">
                                  Conversation Voice Price ID
                                </Typography>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                  {addUsageBillingData.usageBilling.conversationVoicePriceId}
                                </Typography>
                              </Grid>
                            )}
                          </Grid>
                        </Grid>
                      )}
                    </Grid>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Create Monthly Subscription Tab */}
      <TabPanel value={activeTab} index={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Create Monthly Subscription
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Creates a monthly recurring subscription WITHOUT usage-based billing.
              If subscription already exists, returns an error. If subscription does not exist, creates base recurring subscription only.
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <AssemblyLineSearchAndSelect
                  label="Assembly Line"
                  value={createMonthlyAssemblyId}
                  onChange={setCreateMonthlyAssemblyId}
                  required
                  placeholder="Search assembly lines"
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  onClick={handleCreateMonthlySubscription}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <PaymentIcon />}
                >
                  Create Monthly Subscription
                </Button>
              </Grid>
              {createMonthlyData && (
                <Grid item xs={12}>
                  <Paper sx={{ p: 3, mt: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Subscription Information
                    </Typography>
                    <Grid container spacing={2}>
                      {createMonthlyData.subscriptionScheduleId && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">
                            Schedule ID
                          </Typography>
                          <Typography variant="body1">
                            {createMonthlyData.subscriptionScheduleId}
                          </Typography>
                        </Grid>
                      )}
                      {createMonthlyData.status && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">
                            Status
                          </Typography>
                          <Chip
                            label={createMonthlyData.status}
                            color={
                              createMonthlyData.status === 'active'
                                ? 'success'
                                : createMonthlyData.status === 'canceled'
                                  ? 'error'
                                  : 'default'
                            }
                            size="small"
                          />
                        </Grid>
                      )}
                      {createMonthlyData.basePriceId && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">
                            Base Price ID
                          </Typography>
                          <Typography variant="body1">{createMonthlyData.basePriceId}</Typography>
                        </Grid>
                      )}
                      {createMonthlyData.productId && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">
                            Product ID
                          </Typography>
                          <Typography variant="body1">{createMonthlyData.productId}</Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Clear Subscription Tab */}
      <TabPanel value={activeTab} index={7}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Clear Subscription
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Cancels the Stripe subscription/schedule and clears all subscription-related metadata from the assembly line.
              This includes subscription schedule ID, customer ID, product ID, price IDs, meter IDs, and sets setupComplete to false.
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <AssemblyLineSearchAndSelect
                  label="Assembly Line"
                  value={clearSubscriptionAssemblyId}
                  onChange={setClearSubscriptionAssemblyId}
                  required
                  placeholder="Search assembly lines"
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleClearSubscription}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <DeleteIcon />}
                >
                  Clear Subscription
                </Button>
              </Grid>
              {clearSubscriptionResult && (
                <Grid item xs={12}>
                  <Alert severity={clearSubscriptionResult.startsWith('Error') ? 'error' : 'success'}>
                    {clearSubscriptionResult}
                  </Alert>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Subscription Details Tab */}
      <TabPanel value={activeTab} index={8}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Get Subscription Details
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Retrieves comprehensive subscription details for an assembly line including subscription information, product details, usage data, and summary.
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <AssemblyLineSearchAndSelect
                  label="Assembly Line"
                  value={subscriptionDetailsAssemblyId}
                  onChange={setSubscriptionDetailsAssemblyId}
                  required
                  placeholder="Search assembly lines"
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleGetSubscriptionDetails}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <ReceiptIcon />}
                >
                  Get Subscription Details
                </Button>
              </Grid>
              {subscriptionDetailsData && (
                <Grid item xs={12}>
                  <Card variant="outlined" sx={{ mt: 2 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Subscription Details
                      </Typography>

                      {/* Summary Section */}
                      {subscriptionDetailsData.summary && (
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                            Summary
                          </Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                              <Chip
                                label={`Setup Complete: ${subscriptionDetailsData.summary.setupComplete ? 'Yes' : 'No'}`}
                                color={subscriptionDetailsData.summary.setupComplete ? 'success' : 'warning'}
                                sx={{ mb: 1 }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Chip
                                label={`Has Usage Billing: ${subscriptionDetailsData.summary.hasUsageBilling ? 'Yes' : 'No'}`}
                                color={subscriptionDetailsData.summary.hasUsageBilling ? 'success' : 'default'}
                              />
                            </Grid>
                          </Grid>
                        </Box>
                      )}

                      {/* Assembly Line Section */}
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                          Assembly Line Information
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                              ID
                            </Typography>
                            <Typography variant="body1">{subscriptionDetailsData.assemblyLine.id}</Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                              Name
                            </Typography>
                            <Typography variant="body1">{subscriptionDetailsData.assemblyLine.name}</Typography>
                          </Grid>
                          {subscriptionDetailsData.assemblyLine.description && (
                            <Grid item xs={12}>
                              <Typography variant="body2" color="text.secondary">
                                Description
                              </Typography>
                              <Typography variant="body1">{subscriptionDetailsData.assemblyLine.description}</Typography>
                            </Grid>
                          )}
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                              Organization ID
                            </Typography>
                            <Typography variant="body1">{subscriptionDetailsData.assemblyLine.orgId}</Typography>
                          </Grid>
                          {subscriptionDetailsData.assemblyLine.projectType && (
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2" color="text.secondary">
                                Project Type
                              </Typography>
                              <Typography variant="body1">{subscriptionDetailsData.assemblyLine.projectType}</Typography>
                            </Grid>
                          )}
                          {subscriptionDetailsData.assemblyLine.sku && (
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2" color="text.secondary">
                                SKU
                              </Typography>
                              <Chip
                                label={subscriptionDetailsData.assemblyLine.sku}
                                color={
                                  subscriptionDetailsData.assemblyLine.sku === 'ASSEMBLY_ORCHESTRATION'
                                    ? 'primary'
                                    : subscriptionDetailsData.assemblyLine.sku === 'ASSEMBLY_AGENT_SUBSCRIPTION'
                                      ? 'success'
                                      : subscriptionDetailsData.assemblyLine.sku === 'FOUNDRY_APP'
                                        ? 'info'
                                        : 'secondary'
                                }
                                size="small"
                              />
                            </Grid>
                          )}
                          {subscriptionDetailsData.assemblyLine.startDate && (
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2" color="text.secondary">
                                Start Date
                              </Typography>
                              <Typography variant="body1">
                                {moment(subscriptionDetailsData.assemblyLine.startDate / 1000).format('YYYY-MM-DD HH:mm:ss')}
                              </Typography>
                            </Grid>
                          )}
                          {subscriptionDetailsData.assemblyLine.billingAmount !== null && subscriptionDetailsData.assemblyLine.billingAmount !== undefined && (
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2" color="text.secondary">
                                Billing Amount
                              </Typography>
                              <Typography variant="body1">
                                {subscriptionDetailsData.assemblyLine.currency && subscriptionDetailsData.assemblyLine.billingAmount
                                  ? formatCurrency(
                                    Math.round(subscriptionDetailsData.assemblyLine.billingAmount * 100),
                                    subscriptionDetailsData.assemblyLine.currency
                                  )
                                  : subscriptionDetailsData.assemblyLine.billingAmount}
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                      </Box>

                      {/* Subscription Section */}
                      {subscriptionDetailsData.subscription ? (
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                            Subscription Information
                          </Typography>
                          <Grid container spacing={2}>
                            {subscriptionDetailsData.subscription.subscriptionScheduleId && (
                              <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="text.secondary">
                                  Schedule ID
                                </Typography>
                                <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
                                  {subscriptionDetailsData.subscription.subscriptionScheduleId}
                                </Typography>
                              </Grid>
                            )}
                            {subscriptionDetailsData.subscription.status && (
                              <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="text.secondary">
                                  Status
                                </Typography>
                                <Chip
                                  label={subscriptionDetailsData.subscription.status}
                                  color={
                                    subscriptionDetailsData.subscription.status === 'active'
                                      ? 'success'
                                      : subscriptionDetailsData.subscription.status === 'canceled'
                                        ? 'error'
                                        : 'default'
                                  }
                                />
                              </Grid>
                            )}
                            {subscriptionDetailsData.subscription.subscriptionId && (
                              <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="text.secondary">
                                  Subscription ID
                                </Typography>
                                <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
                                  {subscriptionDetailsData.subscription.subscriptionId}
                                </Typography>
                              </Grid>
                            )}
                            {subscriptionDetailsData.subscription.subscriptionStatus && (
                              <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="text.secondary">
                                  Subscription Status
                                </Typography>
                                <Chip
                                  label={subscriptionDetailsData.subscription.subscriptionStatus}
                                  color={
                                    subscriptionDetailsData.subscription.subscriptionStatus === 'active'
                                      ? 'success'
                                      : subscriptionDetailsData.subscription.subscriptionStatus === 'canceled'
                                        ? 'error'
                                        : 'warning'
                                  }
                                />
                              </Grid>
                            )}
                            {subscriptionDetailsData.subscription.startDate && (
                              <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="text.secondary">
                                  Start Date
                                </Typography>
                                <Typography variant="body1">{formatTimestamp(subscriptionDetailsData.subscription.startDate)}</Typography>
                              </Grid>
                            )}
                            {subscriptionDetailsData.subscription.currentPeriodStart && (
                              <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="text.secondary">
                                  Current Period Start
                                </Typography>
                                <Typography variant="body1">{formatTimestamp(subscriptionDetailsData.subscription.currentPeriodStart)}</Typography>
                              </Grid>
                            )}
                            {subscriptionDetailsData.subscription.currentPeriodEnd && (
                              <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="text.secondary">
                                  Current Period End
                                </Typography>
                                <Typography variant="body1">{formatTimestamp(subscriptionDetailsData.subscription.currentPeriodEnd)}</Typography>
                              </Grid>
                            )}
                            {subscriptionDetailsData.subscription.customerId && (
                              <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="text.secondary">
                                  Customer ID
                                </Typography>
                                <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
                                  {subscriptionDetailsData.subscription.customerId}
                                </Typography>
                              </Grid>
                            )}
                            {subscriptionDetailsData.subscription.productId && (
                              <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="text.secondary">
                                  Product ID
                                </Typography>
                                <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
                                  {subscriptionDetailsData.subscription.productId}
                                </Typography>
                              </Grid>
                            )}
                            {subscriptionDetailsData.subscription.basePriceId && (
                              <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="text.secondary">
                                  Base Price ID
                                </Typography>
                                <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
                                  {subscriptionDetailsData.subscription.basePriceId}
                                </Typography>
                              </Grid>
                            )}
                            {subscriptionDetailsData.subscription.setupComplete !== undefined && (
                              <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="text.secondary">
                                  Setup Complete
                                </Typography>
                                <Chip
                                  label={subscriptionDetailsData.subscription.setupComplete ? 'Yes' : 'No'}
                                  color={subscriptionDetailsData.subscription.setupComplete ? 'success' : 'warning'}
                                />
                              </Grid>
                            )}
                            {subscriptionDetailsData.subscription.usageBilling && (
                              <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                  Usage Billing
                                </Typography>
                                <Grid container spacing={2}>
                                  {subscriptionDetailsData.subscription.usageBilling.llmInputTokensMeterId && (
                                    <Grid item xs={12} sm={6}>
                                      <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                                        LLM Input Tokens Meter ID
                                      </Typography>
                                      <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                                        {subscriptionDetailsData.subscription.usageBilling.llmInputTokensMeterId}
                                      </Typography>
                                    </Grid>
                                  )}
                                  {subscriptionDetailsData.subscription.usageBilling.llmInputTokensPriceId && (
                                    <Grid item xs={12} sm={6}>
                                      <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                                        LLM Input Tokens Price ID
                                      </Typography>
                                      <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                                        {subscriptionDetailsData.subscription.usageBilling.llmInputTokensPriceId}
                                      </Typography>
                                    </Grid>
                                  )}
                                  {subscriptionDetailsData.subscription.usageBilling.llmOutputTokensMeterId && (
                                    <Grid item xs={12} sm={6}>
                                      <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                                        LLM Output Tokens Meter ID
                                      </Typography>
                                      <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                                        {subscriptionDetailsData.subscription.usageBilling.llmOutputTokensMeterId}
                                      </Typography>
                                    </Grid>
                                  )}
                                  {subscriptionDetailsData.subscription.usageBilling.llmOutputTokensPriceId && (
                                    <Grid item xs={12} sm={6}>
                                      <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                                        LLM Output Tokens Price ID
                                      </Typography>
                                      <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                                        {subscriptionDetailsData.subscription.usageBilling.llmOutputTokensPriceId}
                                      </Typography>
                                    </Grid>
                                  )}
                                  {subscriptionDetailsData.subscription.usageBilling.conversationVoiceMeterId && (
                                    <Grid item xs={12} sm={6}>
                                      <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                                        Conversation Voice Meter ID
                                      </Typography>
                                      <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                                        {subscriptionDetailsData.subscription.usageBilling.conversationVoiceMeterId}
                                      </Typography>
                                    </Grid>
                                  )}
                                  {subscriptionDetailsData.subscription.usageBilling.conversationVoicePriceId && (
                                    <Grid item xs={12} sm={6}>
                                      <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                                        Conversation Voice Price ID
                                      </Typography>
                                      <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                                        {subscriptionDetailsData.subscription.usageBilling.conversationVoicePriceId}
                                      </Typography>
                                    </Grid>
                                  )}
                                </Grid>
                              </Grid>
                            )}
                            {subscriptionDetailsData.subscription.items && subscriptionDetailsData.subscription.items.length > 0 && (
                              <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                  Subscription Items
                                </Typography>
                                <TableContainer component={Paper} variant="outlined">
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>Item ID</TableCell>
                                        <TableCell>Price ID</TableCell>
                                        <TableCell>Unit Amount</TableCell>
                                        <TableCell>Currency</TableCell>
                                        <TableCell>Usage Type</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {subscriptionDetailsData.subscription.items.map((item, index) => (
                                        <TableRow key={index}>
                                          <TableCell sx={{ wordBreak: 'break-all' }}>{item.id}</TableCell>
                                          <TableCell sx={{ wordBreak: 'break-all' }}>{item.priceId}</TableCell>
                                          <TableCell>
                                            {item.currency && item.unitAmount
                                              ? formatCurrency(item.unitAmount, item.currency)
                                              : item.unitAmount}
                                          </TableCell>
                                          <TableCell>{item.currency.toUpperCase()}</TableCell>
                                          <TableCell>
                                            {item.usageType ? (
                                              <Chip label={item.usageType} size="small" color="info" />
                                            ) : (
                                              <Chip label="Base" size="small" />
                                            )}
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              </Grid>
                            )}
                          </Grid>
                        </Box>
                      ) : (
                        <Box sx={{ mb: 3 }}>
                          <Alert severity="info">
                            {subscriptionDetailsData.message || 'No subscription found for this assembly line'}
                          </Alert>
                        </Box>
                      )}

                      {/* Product Section */}
                      {subscriptionDetailsData.product && (
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                            Product Information
                          </Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2" color="text.secondary">
                                Product ID
                              </Typography>
                              <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
                                {subscriptionDetailsData.product.id}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2" color="text.secondary">
                                Name
                              </Typography>
                              <Typography variant="body1">{subscriptionDetailsData.product.name}</Typography>
                            </Grid>
                            {subscriptionDetailsData.product.description && (
                              <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary">
                                  Description
                                </Typography>
                                <Typography variant="body1">{subscriptionDetailsData.product.description}</Typography>
                              </Grid>
                            )}
                            {subscriptionDetailsData.product.active !== undefined && (
                              <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="text.secondary">
                                  Active
                                </Typography>
                                <Chip
                                  label={subscriptionDetailsData.product.active ? 'Yes' : 'No'}
                                  color={subscriptionDetailsData.product.active ? 'success' : 'default'}
                                />
                              </Grid>
                            )}
                            {subscriptionDetailsData.product.created && (
                              <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="text.secondary">
                                  Created
                                </Typography>
                                <Typography variant="body1">
                                  {moment.unix(subscriptionDetailsData.product.created).format('YYYY-MM-DD HH:mm:ss')}
                                </Typography>
                              </Grid>
                            )}
                            {subscriptionDetailsData.product.metadata && Object.keys(subscriptionDetailsData.product.metadata).length > 0 && (
                              <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                  Metadata
                                </Typography>
                                <Paper variant="outlined" sx={{ p: 2 }}>
                                  {Object.entries(subscriptionDetailsData.product.metadata).map(([key, value]) => (
                                    <Box key={key} sx={{ mb: 1 }}>
                                      <Typography variant="body2" component="span" sx={{ fontWeight: 'bold' }}>
                                        {key}:
                                      </Typography>{' '}
                                      <Typography variant="body2" component="span">
                                        {value}
                                      </Typography>
                                    </Box>
                                  ))}
                                </Paper>
                              </Grid>
                            )}
                          </Grid>
                        </Box>
                      )}

                      {/* Current Usage Section */}
                      {subscriptionDetailsData.currentUsage && (
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                            Current Usage (Current Billing Period)
                          </Typography>

                          {/* Metadata */}
                          {subscriptionDetailsData.currentUsage.metadata && (
                            <Card variant="outlined" sx={{ mb: 2 }}>
                              <CardContent>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                  Subscription Metadata
                                </Typography>
                                <Grid container spacing={2}>
                                  {subscriptionDetailsData.currentUsage.metadata.scheduleId && (
                                    <Grid item xs={12} sm={6}>
                                      <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                                        Schedule ID
                                      </Typography>
                                      <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                                        {subscriptionDetailsData.currentUsage.metadata.scheduleId}
                                      </Typography>
                                    </Grid>
                                  )}
                                  {subscriptionDetailsData.currentUsage.metadata.startDate && (
                                    <Grid item xs={12} sm={6}>
                                      <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                                        Start Date
                                      </Typography>
                                      <Typography variant="body2">
                                        {formatTimestamp(subscriptionDetailsData.currentUsage.metadata.startDate)}
                                      </Typography>
                                    </Grid>
                                  )}
                                  {subscriptionDetailsData.currentUsage.metadata.status && (
                                    <Grid item xs={12} sm={6}>
                                      <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                                        Status
                                      </Typography>
                                      <Chip
                                        label={subscriptionDetailsData.currentUsage.metadata.status}
                                        size="small"
                                        color={
                                          subscriptionDetailsData.currentUsage.metadata.status === 'active'
                                            ? 'success'
                                            : subscriptionDetailsData.currentUsage.metadata.status === 'not_started'
                                              ? 'warning'
                                              : 'default'
                                        }
                                      />
                                    </Grid>
                                  )}
                                </Grid>
                              </CardContent>
                            </Card>
                          )}

                          {/* Usage Cards */}
                          <Grid container spacing={2}>
                            {/* Legacy format: llm_tokens */}
                            {subscriptionDetailsData.currentUsage.llm_tokens && (
                              <Grid item xs={12} sm={6}>
                                <Card variant="outlined">
                                  <CardContent>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                      LLM Tokens
                                    </Typography>
                                    <Typography variant="h6">
                                      {subscriptionDetailsData.currentUsage.llm_tokens.total.toLocaleString()}
                                    </Typography>
                                    {(subscriptionDetailsData.currentUsage.llm_tokens.startTime ||
                                      subscriptionDetailsData.currentUsage.llm_tokens.start_time) && (
                                        <Typography variant="caption" color="text.secondary">
                                          Start:{' '}
                                          {formatTimestamp(
                                            subscriptionDetailsData.currentUsage.llm_tokens.startTime ||
                                            subscriptionDetailsData.currentUsage.llm_tokens.start_time ||
                                            0
                                          )}
                                        </Typography>
                                      )}
                                    {(subscriptionDetailsData.currentUsage.llm_tokens.endTime ||
                                      subscriptionDetailsData.currentUsage.llm_tokens.end_time) && (
                                        <Typography variant="caption" color="text.secondary" display="block">
                                          End:{' '}
                                          {formatTimestamp(
                                            subscriptionDetailsData.currentUsage.llm_tokens.endTime ||
                                            subscriptionDetailsData.currentUsage.llm_tokens.end_time ||
                                            0
                                          )}
                                        </Typography>
                                      )}
                                  </CardContent>
                                </Card>
                              </Grid>
                            )}
                            {/* Legacy format: conversation_voice */}
                            {subscriptionDetailsData.currentUsage.conversation_voice && (
                              <Grid item xs={12} sm={6}>
                                <Card variant="outlined">
                                  <CardContent>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                      Conversation Voice
                                    </Typography>
                                    <Typography variant="h6">
                                      {subscriptionDetailsData.currentUsage.conversation_voice.total.toLocaleString()} minutes
                                    </Typography>
                                    {(subscriptionDetailsData.currentUsage.conversation_voice.startTime ||
                                      subscriptionDetailsData.currentUsage.conversation_voice.start_time) && (
                                        <Typography variant="caption" color="text.secondary">
                                          Start:{' '}
                                          {formatTimestamp(
                                            subscriptionDetailsData.currentUsage.conversation_voice.startTime ||
                                            subscriptionDetailsData.currentUsage.conversation_voice.start_time ||
                                            0
                                          )}
                                        </Typography>
                                      )}
                                    {(subscriptionDetailsData.currentUsage.conversation_voice.endTime ||
                                      subscriptionDetailsData.currentUsage.conversation_voice.end_time) && (
                                        <Typography variant="caption" color="text.secondary" display="block">
                                          End:{' '}
                                          {formatTimestamp(
                                            subscriptionDetailsData.currentUsage.conversation_voice.endTime ||
                                            subscriptionDetailsData.currentUsage.conversation_voice.end_time ||
                                            0
                                          )}
                                        </Typography>
                                      )}
                                  </CardContent>
                                </Card>
                              </Grid>
                            )}
                            {/* New format: price_xxxxx keys */}
                            {Object.keys(subscriptionDetailsData.currentUsage)
                              .filter((key) => key.startsWith('price_') && key !== 'metadata')
                              .map((key) => {
                                const item = subscriptionDetailsData.currentUsage?.[key] as UsageDataItem | undefined
                                if (!item) return null
                                return (
                                  <Grid item xs={12} sm={6} key={key}>
                                    <Card variant="outlined">
                                      <CardContent>
                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                          {key}
                                        </Typography>
                                        <Typography variant="h6">{item.total.toLocaleString()}</Typography>
                                        {(item.startTime || item.start_time) && (
                                          <Typography variant="caption" color="text.secondary">
                                            Start: {formatTimestamp(item.startTime || item.start_time || 0)}
                                          </Typography>
                                        )}
                                        {(item.endTime || item.end_time) && (
                                          <Typography variant="caption" color="text.secondary" display="block">
                                            End: {formatTimestamp(item.endTime || item.end_time || 0)}
                                          </Typography>
                                        )}
                                      </CardContent>
                                    </Card>
                                  </Grid>
                                )
                              })}
                          </Grid>
                        </Box>
                      )}

                      {/* Token Pricing Section */}
                      {subscriptionDetailsData.tokenPricing && (
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                            Token Pricing
                          </Typography>
                          <Grid container spacing={2}>
                            {subscriptionDetailsData.tokenPricing.inputTokensPerMillion !== undefined && (
                              <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="text.secondary">
                                  Input Tokens per Million
                                </Typography>
                                <Typography variant="body1">
                                  ${subscriptionDetailsData.tokenPricing.inputTokensPerMillion.toFixed(3)}
                                </Typography>
                              </Grid>
                            )}
                            {subscriptionDetailsData.tokenPricing.outputTokensPerMillion !== undefined && (
                              <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="text.secondary">
                                  Output Tokens per Million
                                </Typography>
                                <Typography variant="body1">
                                  ${subscriptionDetailsData.tokenPricing.outputTokensPerMillion.toFixed(2)}
                                </Typography>
                              </Grid>
                            )}
                            {subscriptionDetailsData.tokenPricing.voiceMinutesPerMinute !== undefined && (
                              <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="text.secondary">
                                  Voice Minutes per Minute
                                </Typography>
                                <Typography variant="body1">
                                  ${subscriptionDetailsData.tokenPricing.voiceMinutesPerMinute.toFixed(2)}
                                </Typography>
                              </Grid>
                            )}
                            {subscriptionDetailsData.tokenPricing.totalCredits !== null && subscriptionDetailsData.tokenPricing.totalCredits !== undefined && (
                              <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="text.secondary">
                                  Total Credits
                                </Typography>
                                <Typography variant="body1">{subscriptionDetailsData.tokenPricing.totalCredits}</Typography>
                              </Grid>
                            )}
                            {subscriptionDetailsData.tokenPricing.totalCreditsMonetaryValue !== null &&
                              subscriptionDetailsData.tokenPricing.totalCreditsMonetaryValue !== undefined && (
                                <Grid item xs={12} sm={6}>
                                  <Typography variant="body2" color="text.secondary">
                                    Total Credits Value
                                  </Typography>
                                  <Typography variant="body1">
                                    {formatCurrency(subscriptionDetailsData.tokenPricing.totalCreditsMonetaryValue, 'usd')}
                                  </Typography>
                                </Grid>
                              )}
                            {subscriptionDetailsData.tokenPricing.creditsStartTime !== null &&
                              subscriptionDetailsData.tokenPricing.creditsStartTime !== undefined && (
                                <Grid item xs={12} sm={6}>
                                  <Typography variant="body2" color="text.secondary">
                                    Credits Start Time
                                  </Typography>
                                  <Typography variant="body1">{formatTimestamp(subscriptionDetailsData.tokenPricing.creditsStartTime)}</Typography>
                                </Grid>
                              )}
                            {subscriptionDetailsData.tokenPricing.creditsEndTime !== null &&
                              subscriptionDetailsData.tokenPricing.creditsEndTime !== undefined && (
                                <Grid item xs={12} sm={6}>
                                  <Typography variant="body2" color="text.secondary">
                                    Credits End Time
                                  </Typography>
                                  <Typography variant="body1">{formatTimestamp(subscriptionDetailsData.tokenPricing.creditsEndTime)}</Typography>
                                </Grid>
                              )}
                          </Grid>
                        </Box>
                      )}

                      {/* Credit Balance Section */}
                      {subscriptionDetailsData.creditBalance && (
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                            Credit Balance
                          </Typography>
                          <Grid container spacing={2}>
                            {subscriptionDetailsData.creditBalance.availableBalance !== null &&
                              subscriptionDetailsData.creditBalance.availableBalance !== undefined && (
                                <Grid item xs={12} sm={6}>
                                  <Typography variant="body2" color="text.secondary">
                                    Available Balance
                                  </Typography>
                                  <Typography variant="body1">
                                    {subscriptionDetailsData.creditBalance.currency &&
                                      subscriptionDetailsData.creditBalance.availableBalanceInDollars !== null &&
                                      subscriptionDetailsData.creditBalance.availableBalanceInDollars !== undefined
                                      ? formatCurrency(
                                        Math.round(subscriptionDetailsData.creditBalance.availableBalanceInDollars * 100),
                                        subscriptionDetailsData.creditBalance.currency
                                      )
                                      : formatCurrency(subscriptionDetailsData.creditBalance.availableBalance, subscriptionDetailsData.creditBalance.currency || 'usd')}
                                  </Typography>
                                </Grid>
                              )}
                            {subscriptionDetailsData.creditBalance.ledgerBalance !== null &&
                              subscriptionDetailsData.creditBalance.ledgerBalance !== undefined && (
                                <Grid item xs={12} sm={6}>
                                  <Typography variant="body2" color="text.secondary">
                                    Ledger Balance
                                  </Typography>
                                  <Typography variant="body1">
                                    {subscriptionDetailsData.creditBalance.currency &&
                                      subscriptionDetailsData.creditBalance.ledgerBalanceInDollars !== null &&
                                      subscriptionDetailsData.creditBalance.ledgerBalanceInDollars !== undefined
                                      ? formatCurrency(
                                        Math.round(subscriptionDetailsData.creditBalance.ledgerBalanceInDollars * 100),
                                        subscriptionDetailsData.creditBalance.currency
                                      )
                                      : formatCurrency(subscriptionDetailsData.creditBalance.ledgerBalance, subscriptionDetailsData.creditBalance.currency || 'usd')}
                                  </Typography>
                                </Grid>
                              )}
                            {subscriptionDetailsData.creditBalance.totalGrants !== null &&
                              subscriptionDetailsData.creditBalance.totalGrants !== undefined && (
                                <Grid item xs={12} sm={6}>
                                  <Typography variant="body2" color="text.secondary">
                                    Total Grants
                                  </Typography>
                                  <Typography variant="body1">{subscriptionDetailsData.creditBalance.totalGrants}</Typography>
                                </Grid>
                              )}
                            {subscriptionDetailsData.creditBalance.currency && (
                              <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="text.secondary">
                                  Currency
                                </Typography>
                                <Typography variant="body1">{subscriptionDetailsData.creditBalance.currency.toUpperCase()}</Typography>
                              </Grid>
                            )}
                          </Grid>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Create Credit Grant Tab */}
      <TabPanel value={activeTab} index={9}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Create Credit Grant
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Create a billing credit grant for prepaid or promotional credits
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <AssemblyLineSearchAndSelect
                  label="Assembly Line"
                  value={creditGrantAssemblyId}
                  onChange={setCreditGrantAssemblyId}
                  required
                  placeholder="Search assembly lines"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Amount"
                  type="number"
                  value={creditGrantAmountDollars}
                  onChange={(e) => setCreditGrantAmountDollars(e.target.value)}
                  required
                  inputProps={{ min: 0, step: 0.01 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  helperText="Amount in dollars (will be converted to cents)"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Currency"
                  value={creditGrantForm.currency || 'usd'}
                  onChange={(e) => setCreditGrantForm({ ...creditGrantForm, currency: e.target.value })}
                  placeholder="usd"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={creditGrantForm.category || 'paid'}
                    onChange={(e) => setCreditGrantForm({ ...creditGrantForm, category: e.target.value as 'paid' | 'promotional' })}
                    label="Category"
                  >
                    <MenuItem value="paid">Paid</MenuItem>
                    <MenuItem value="promotional">Promotional</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Name (Optional)"
                  value={creditGrantForm.name || ''}
                  onChange={(e) => setCreditGrantForm({ ...creditGrantForm, name: e.target.value || null })}
                  placeholder="Prepaid Credits"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DateTimePicker
                  label="Effective At (Optional)"
                  value={creditGrantEffectiveAt}
                  onChange={(newValue) => setCreditGrantEffectiveAt(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      helperText: 'When credits become effective (will be converted to Unix timestamp)',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DateTimePicker
                  label="Expires At (Optional)"
                  value={creditGrantExpiresAt}
                  onChange={(newValue) => setCreditGrantExpiresAt(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      helperText: 'When credits expire (will be converted to Unix timestamp)',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Priority (optional)"
                  type="number"
                  value={creditGrantForm.priority || ''}
                  onChange={(e) => setCreditGrantForm({ ...creditGrantForm, priority: e.target.value ? parseInt(e.target.value) : null })}
                  placeholder="50"
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleCreateCreditGrant}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <CreditCardIcon />}
                >
                  Create Credit Grant
                </Button>
              </Grid>
              {creditGrantResult && (
                <Grid item xs={12}>
                  <Card variant="outlined" sx={{ mt: 2 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Credit Grant Created
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            ID
                          </Typography>
                          <Typography variant="body1">{creditGrantResult.id}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            Amount
                          </Typography>
                          <Typography variant="body1">{formatCurrency(creditGrantResult.amount, creditGrantResult.currency)}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            Category
                          </Typography>
                          <Chip label={creditGrantResult.category} color={creditGrantResult.category === 'PAID' ? 'primary' : 'secondary'} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            State
                          </Typography>
                          <Chip
                            label={creditGrantResult.state}
                            color={
                              creditGrantResult.state === 'GRANTED'
                                ? 'success'
                                : creditGrantResult.state === 'EXPIRED' || creditGrantResult.state === 'VOIDED'
                                  ? 'error'
                                  : 'default'
                            }
                          />
                        </Grid>
                        {creditGrantResult.name && (
                          <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">
                              Name
                            </Typography>
                            <Typography variant="body1">{creditGrantResult.name}</Typography>
                          </Grid>
                        )}
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Credit Balance Tab */}
      <TabPanel value={activeTab} index={10}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Get Credit Balance Summary
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Retrieve credit balance summary including available balance and credit grants
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <AssemblyLineSearchAndSelect
                  label="Assembly Line"
                  value={creditBalanceAssemblyId}
                  onChange={setCreditBalanceAssemblyId}
                  required
                  placeholder="Search assembly lines"
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleGetCreditBalance}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <AccountBalanceIcon />}
                >
                  Get Credit Balance
                </Button>
              </Grid>
              {creditBalanceData && (
                <Grid item xs={12}>
                  <Card variant="outlined" sx={{ mt: 2 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Credit Balance Summary
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                            Balance
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            Available Balance
                          </Typography>
                          <Typography variant="body1">
                            {creditBalanceData.balance.availableBalanceInDollars !== null && creditBalanceData.balance.availableBalanceInDollars !== undefined
                              ? formatCurrency(Math.round(creditBalanceData.balance.availableBalanceInDollars * 100), creditBalanceData.balance.currency)
                              : formatCurrency(creditBalanceData.balance.availableBalance, creditBalanceData.balance.currency)}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            Ledger Balance
                          </Typography>
                          <Typography variant="body1">
                            {creditBalanceData.balance.ledgerBalanceInDollars !== null && creditBalanceData.balance.ledgerBalanceInDollars !== undefined
                              ? formatCurrency(Math.round(creditBalanceData.balance.ledgerBalanceInDollars * 100), creditBalanceData.balance.currency)
                              : formatCurrency(creditBalanceData.balance.ledgerBalance, creditBalanceData.balance.currency)}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            Currency
                          </Typography>
                          <Typography variant="body1">{creditBalanceData.balance.currency.toUpperCase()}</Typography>
                        </Grid>
                        {creditBalanceData.grants && creditBalanceData.grants.length > 0 && (
                          <>
                            <Grid item xs={12}>
                              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mt: 2 }}>
                                Credit Grants ({creditBalanceData.grants.length})
                              </Typography>
                            </Grid>
                            {creditBalanceData.grants.map((grant) => (
                              <Grid item xs={12} key={grant.id}>
                                <Card variant="outlined">
                                  <CardContent>
                                    <Grid container spacing={2}>
                                      <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" color="text.secondary">
                                          ID
                                        </Typography>
                                        <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
                                          {grant.id}
                                        </Typography>
                                      </Grid>
                                      <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" color="text.secondary">
                                          Amount
                                        </Typography>
                                        <Typography variant="body1">{formatCurrency(grant.amount, grant.currency)}</Typography>
                                      </Grid>
                                      <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" color="text.secondary">
                                          Category
                                        </Typography>
                                        <Chip label={grant.category} color={grant.category === 'PAID' ? 'primary' : 'secondary'} size="small" />
                                      </Grid>
                                      <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" color="text.secondary">
                                          State
                                        </Typography>
                                        <Chip
                                          label={grant.state}
                                          color={
                                            grant.state === 'GRANTED'
                                              ? 'success'
                                              : grant.state === 'EXPIRED' || grant.state === 'VOIDED'
                                                ? 'error'
                                                : 'default'
                                          }
                                          size="small"
                                        />
                                      </Grid>
                                      {grant.name && (
                                        <Grid item xs={12} sm={6}>
                                          <Typography variant="body2" color="text.secondary">
                                            Name
                                          </Typography>
                                          <Typography variant="body1">{grant.name}</Typography>
                                        </Grid>
                                      )}
                                      {grant.effectiveAt && (
                                        <Grid item xs={12} sm={6}>
                                          <Typography variant="body2" color="text.secondary">
                                            Effective At
                                          </Typography>
                                          <Typography variant="body1">{formatTimestamp(grant.effectiveAt)}</Typography>
                                        </Grid>
                                      )}
                                      {grant.expiresAt && (
                                        <Grid item xs={12} sm={6}>
                                          <Typography variant="body2" color="text.secondary">
                                            Expires At
                                          </Typography>
                                          <Typography variant="body1">{formatTimestamp(grant.expiresAt)}</Typography>
                                        </Grid>
                                      )}
                                      {grant.createdAt && (
                                        <Grid item xs={12} sm={6}>
                                          <Typography variant="body2" color="text.secondary">
                                            Created At
                                          </Typography>
                                          <Typography variant="body1">{formatTimestamp(grant.createdAt)}</Typography>
                                        </Grid>
                                      )}
                                    </Grid>
                                  </CardContent>
                                </Card>
                              </Grid>
                            ))}
                          </>
                        )}
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Manage Credit Grants Tab */}
      <TabPanel value={activeTab} index={11}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Manage Credit Grants
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              List, update, expire, or void credit grants for an assembly line
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <AssemblyLineSearchAndSelect
                  label="Assembly Line"
                  value={creditGrantsAssemblyId}
                  onChange={setCreditGrantsAssemblyId}
                  required
                  placeholder="Search assembly lines"
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleListCreditGrants}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <EditIcon />}
                >
                  List Credit Grants
                </Button>
              </Grid>
              {creditGrantsList.length > 0 && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                      Credit Grants ({creditGrantsList.length})
                    </Typography>
                  </Grid>
                  {creditGrantsList.map((grant) => (
                    <Grid item xs={12} key={grant.id}>
                      <Card variant="outlined">
                        <CardContent>
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2" color="text.secondary">
                                ID
                              </Typography>
                              <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
                                {grant.id}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2" color="text.secondary">
                                Amount
                              </Typography>
                              <Typography variant="body1">{formatCurrency(grant.amount, grant.currency)}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2" color="text.secondary">
                                Category
                              </Typography>
                              <Chip label={grant.category} color={grant.category === 'PAID' ? 'primary' : 'secondary'} size="small" />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2" color="text.secondary">
                                State
                              </Typography>
                              <Chip
                                label={grant.state}
                                color={
                                  grant.state === 'GRANTED'
                                    ? 'success'
                                    : grant.state === 'EXPIRED' || grant.state === 'VOIDED'
                                      ? 'error'
                                      : 'default'
                                }
                                size="small"
                              />
                            </Grid>
                            {grant.name && (
                              <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="text.secondary">
                                  Name
                                </Typography>
                                <Typography variant="body1">{grant.name}</Typography>
                              </Grid>
                            )}
                            {grant.expiresAt && (
                              <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="text.secondary">
                                  Expires At
                                </Typography>
                                <Typography variant="body1">{formatTimestamp(grant.expiresAt)}</Typography>
                              </Grid>
                            )}
                            <Grid item xs={12}>
                              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<EditIcon />}
                                  onClick={() => {
                                    setSelectedCreditGrant(grant)
                                    setUpdateCreditGrantExpiresAt(grant.expiresAt ? moment.unix(grant.expiresAt) : null)
                                    setUpdateCreditGrantForm({
                                      metadata: grant.metadata || {},
                                    })
                                  }}
                                >
                                  Update
                                </Button>
                                {grant.state !== 'EXPIRED' && grant.state !== 'VOIDED' && (
                                  <>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color="warning"
                                      startIcon={<BlockIcon />}
                                      onClick={() => handleExpireCreditGrant(grant.id)}
                                      disabled={loading}
                                    >
                                      Expire
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color="error"
                                      startIcon={<CancelIcon />}
                                      onClick={() => handleVoidCreditGrant(grant.id)}
                                      disabled={loading}
                                    >
                                      Void
                                    </Button>
                                  </>
                                )}
                              </Box>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </>
              )}
              {selectedCreditGrant && (
                <Grid item xs={12}>
                  <Card variant="outlined" sx={{ mt: 2, bgcolor: 'background.default' }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Update Credit Grant: {selectedCreditGrant.id}
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <DateTimePicker
                            label="Expires At (Optional)"
                            value={updateCreditGrantExpiresAt}
                            onChange={(newValue) => setUpdateCreditGrantExpiresAt(newValue)}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                helperText: 'When credits expire (will be converted to Unix timestamp)',
                              },
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Button variant="contained" color="primary" onClick={handleUpdateCreditGrant} disabled={loading}>
                            Update Credit Grant
                          </Button>
                          <Button
                            variant="outlined"
                            color="secondary"
                            onClick={() => {
                              setSelectedCreditGrant(null)
                              setUpdateCreditGrantExpiresAt(null)
                              setUpdateCreditGrantForm({ metadata: {} })
                            }}
                            sx={{ ml: 1 }}
                          >
                            Cancel
                          </Button>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>
    </Box>
  )
}

